  /**
 * Service para procesar cálculos de planilla basados en el array unificado de empleados activos
 * Recibe el array completo y procesa solo los trabajadores para modificar planillas según tareos
 */

import {
  obtenerFechasLaboralesEfectivasEnRango,
  obtenerPeriodoLaboralEnRango,
} from '../utils/fechaImportacion';
import {
  CODIGO_PLAME_BONIFICACION_NOCTURNA,
  debeOmitir0309EnProcesamientoPlantilla,
  debeOmitir0309DeTareos,
  debeUsarMontoEstaticoCompleto0309,
  logDiagnosticoBonificacionNocturna,
  logDiagnosticoBonificacionNocturnaVisible,
  tasaNocturnaPriorizaSobreConceptoEstatico,
} from '../utils/bonificacionNocturnaPlanilla';
import { aplicarOverridesTareos } from '../utils/tareoEventoEfectivo';
import { usaHorasFijas8Simple } from '../utils/horasTareoSimple';
import {
  tramoSueldoPlameActivo,
  aplicarTramoSueldoAIngresos,
  destinoAplicaPlame,
  destinoAplicaInterna,
  configInternaDesdeSueldoTramo,
} from '../utils/sueldoTramosPlame';
import { deduplicarConceptosPorPlame } from '../utils/recalculoConsideracionesConceptos';
import {
  conceptoVaABoletaPlame,
  fusionarAjustesEnInterna,
} from '../utils/ajusteConceptoDestino';
import {
  conceptoEstaEnEstaticos,
  debeConservarMontoEstaticoFicha,
  esRegimenAgrarioDeEmpleado,
} from '../utils/conceptosEstaticosPlanilla';

const planillaCalculoService = {
  
  /**
   * Procesa el array unificado y aplica lógica de planilla a cada empleado activo
   * @param {Array} arrayUnificado - Array completo con metadatos, contexto, APIs y empleados
   * @returns {Object} - Resultado del procesamiento con empleados procesados y estadísticas
   */
  procesarEmpleadosParaPlanilla: async (arrayUnificado) => {
    try {
      console.log('🔄 === INICIANDO PROCESAMIENTO DE EMPLEADOS PARA PLANILLA ===');
      console.log('📊 Array unificado recibido:', arrayUnificado);
      //console.log('📋 Total elementos en array:', arrayUnificado.length);
      
      const primerEmpleado = arrayUnificado.find((e) => e.tipo === 'empleado_activo_con_apis');
      const feriadosEnDatosAPI = primerEmpleado?.datosAPIs?.feriadosPagoDoble;
      if (Array.isArray(feriadosEnDatosAPI)) {
        console.log(`🎉 Feriados pago doble en datosAPIs: ${feriadosEnDatosAPI.length} fecha(s)`);
      }

      // 💤 VERIFICAR Y MOSTRAR COMPENSACIONES
      const seccionCompensaciones = arrayUnificado.find(elemento => elemento.tipo === 'compensaciones_meses_pasados');
      if (seccionCompensaciones) {
        //console.log('\n💤 === COMPENSACIONES DE MESES PASADOS DETECTADAS ===');
        //console.log(`📊 Total compensaciones: ${seccionCompensaciones.total_compensaciones}`);
        //console.log(`⏰ Total horas compensadas: ${seccionCompensaciones.total_horas_compensadas}h`);
        //console.log(`✅ Compensaciones activas (>0h): ${seccionCompensaciones.compensaciones_activas}`);
        //console.log(`📋 Empleados con compensaciones: ${Object.keys(seccionCompensaciones.resumen_por_empleado || {}).length}`);
        
        if (seccionCompensaciones.total_horas_compensadas > 0) {
          //console.log('📄 Detalle de compensaciones activas:');
          Object.entries(seccionCompensaciones.resumen_por_empleado || {}).forEach(([empleadoId, data]) => {
            if (data.horas_compensadas > 0) {
              //console.log(`  • ${data.empleado_nombre}: ${data.horas_compensadas}h (ID: ${empleadoId})`);
            }
          });
        }
        //console.log('✅ Compensaciones cargadas correctamente en el array unificado\n');
      } else {
        //console.log('💤 No se detectaron compensaciones en el array unificado');
      }
      
      // ✅ FILTRAR SOLO LOS EMPLEADOS ACTIVOS (tipo: 'empleado_activo_con_apis')
      const empleados = arrayUnificado.filter(elemento => 
        elemento.tipo === 'empleado_activo_con_apis'
      );
      
      console.log(`👥 Empleados activos encontrados: ${empleados.length}`);
      
      // ✅ OBTENER CONTEXTO Y METADATOS DEL ARRAY
      const metadatos = arrayUnificado.find(e => e.tipo === 'metadatos_sistema');
      const contexto = arrayUnificado.find(e => e.tipo === 'contexto_empresarial');
      const apis = arrayUnificado.find(e => e.tipo === 'resumen_apis_disponibles');
      const estadisticas = arrayUnificado.find(e => e.tipo === 'estadisticas_generales');
      
      //console.log('📈 Metadatos disponibles:', !!metadatos);
      //console.log('🏢 Contexto empresarial disponible:', !!contexto);
      //console.log('📊 APIs disponibles:', !!apis);
      //console.log('📈 Estadísticas disponibles:', !!estadisticas);
      
      // ✅ INICIALIZAR VARIABLES DE CONTROL
      const resultados = {
        empleadosProcesados: [],
        empleadosConErrores: [],
        estadisticasProcesamiento: {
          totalEmpleados: empleados.length,
          procesadosExitosos: 0,
          procesadosConErrores: 0,
          tiempoInicio: new Date().toISOString(),
          tiempoFin: null
        }
      };
      
      console.log('🚀 Iniciando procesamiento de empleados...');
      
      // 🔄 FOR LOOP PRINCIPAL - PROCESAR CADA EMPLEADO
      for (let index = 0; index < empleados.length; index++) {
        const empleado = empleados[index];
        try {
          console.log(`\n👤 === PROCESANDO EMPLEADO ${index + 1}/${empleados.length} ===`);
          console.log(`🆔 ID: ${empleado.empleado_id}`);
          console.log(`👤 Nombre: ${empleado.empleado_nombre}`);
          console.log(`📍 Posición en array: ${empleado.posicion_en_array}`);
          console.log(`🎯 Posición entre activos: ${empleado.empleado_posicion_activos}`);
          
          // 📊 INFORMACIÓN DISPONIBLE PARA PROCESAMIENTO
          console.log('📊 Datos disponibles para procesamiento:');
          console.log(`  🏢 Empresa: ${empleado.datosEmpresa?.name || 'N/A'}`);
          console.log(`  💰 Sueldo: S/ ${empleado.datosSalariales?.sueldoBasico || 0}`);
          console.log(`  🏛️ Régimen: ${empleado.datosSalariales?.regimenLaboral || 'N/A'}`);
          console.log(`  💳 Sistema: ${empleado.datosSalariales?.sistemaPensionario || 'N/A'}`);
          if (empleado.datosSalariales?.sistemaPensionario === 'AFP') {
            console.log(`    📋 AFP: ${empleado.datosSalariales?.afp || 'Sin AFP'}`);
            if (empleado.datosSalariales?.cuspp) {
              console.log(`    🆔 CUSPP: ${empleado.datosSalariales.cuspp}`);
            }
          }
          console.log(`  📋 Conceptos: ${empleado.conceptosUsuario?.ingresos?.length || 0}⬆️ + ${empleado.conceptosUsuario?.descuentos?.length || 0}⬇️ + ${empleado.conceptosUsuario?.aportes?.length || 0}🏛️`);
          console.log(`  ⏰ Tareos: ${empleado.resumenTareos?.total_tareos || 0} registros`);
          console.log(`  🎯 Días trabajados: ${empleado.resumenTareos?.dias_trabajados || 0}`);
          console.log(`  💼 Horas totales: ${empleado.resumenTareos?.horas_totales || 0}h`);
          console.log(`  🔐 Autorizaciones: ${empleado.resumenAutorizaciones?.total_autorizaciones || 0} registros`);
          
          // 🎯 AQUÍ VA TU LÓGICA DE PROCESAMIENTO
          console.log('🔧 === APLICANDO LÓGICA DE PLANILLA ===');
          
          // ⚠️ PLACEHOLDER - AQUÍ IMPLEMENTARÁS TU LÓGICA
          const empleadoProcesado = await aplicarLogicaPlanilla(empleado, {
            metadatos,
            contexto,
            apis,
            estadisticas,
            arrayUnificado, // 💤 Agregar array completo para acceso a compensaciones
            index,
            total: empleados.length
          });
          
          // ✅ GUARDAR RESULTADO EXITOSO
          resultados.empleadosProcesados.push(empleadoProcesado);
          resultados.estadisticasProcesamiento.procesadosExitosos++;
          
          console.log(`✅ Empleado ${empleado.empleado_nombre} procesado exitosamente`);
          
        } catch (error) {
          console.error(`❌ Error procesando empleado ${empleado.empleado_nombre}:`, error);
          
          // ❌ GUARDAR ERROR
          resultados.empleadosConErrores.push({
            empleado: empleado,
            error: error.message,
            stack: error.stack,
            timestamp: new Date().toISOString()
          });
          resultados.estadisticasProcesamiento.procesadosConErrores++;
        }
      }
      
      // ✅ FINALIZAR PROCESAMIENTO
      resultados.estadisticasProcesamiento.tiempoFin = new Date().toISOString();
      
      console.log('\n🏁 === PROCESAMIENTO COMPLETADO ===');
      console.log(`✅ Empleados procesados exitosamente: ${resultados.estadisticasProcesamiento.procesadosExitosos}`);
      console.log(`❌ Empleados con errores: ${resultados.estadisticasProcesamiento.procesadosConErrores}`);
      console.log(`📊 Porcentaje de éxito: ${((resultados.estadisticasProcesamiento.procesadosExitosos / empleados.length) * 100).toFixed(1)}%`);
      
      return {
        success: true,
        data: resultados,
        message: `Procesamiento completado: ${resultados.estadisticasProcesamiento.procesadosExitosos}/${empleados.length} empleados procesados exitosamente`
      };
      
    } catch (error) {
      console.error('❌ Error en procesamiento general:', error);
      return {
        success: false,
        error: error.message,
        stack: error.stack
      };
    }
  },
  
  /**
   * Obtiene estadísticas resumidas del procesamiento
   * @param {Object} resultados - Resultados del procesamiento
   * @returns {Object} - Estadísticas resumidas
   */
  obtenerEstadisticasResumen: (resultados) => {
    const stats = resultados.estadisticasProcesamiento;
    const tiempoInicio = new Date(stats.tiempoInicio);
    const tiempoFin = new Date(stats.tiempoFin);
    const duracionMs = tiempoFin - tiempoInicio;
    
    return {
      totalEmpleados: stats.totalEmpleados,
      procesadosExitosos: stats.procesadosExitosos,
      procesadosConErrores: stats.procesadosConErrores,
      porcentajeExito: ((stats.procesadosExitosos / stats.totalEmpleados) * 100).toFixed(1),
      duracionSegundos: (duracionMs / 1000).toFixed(2),
      empleadosPorSegundo: (stats.totalEmpleados / (duracionMs / 1000)).toFixed(2)
    };
  }
};

/** Fecha comparable YYYY-MM-DD (tareo y config. feriados pago doble). */
function normalizarFechaYYYYMMDD(valor) {
  if (valor == null || valor === '') return null;
  const s = String(valor).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return null;
}

/**
 * 🎯 FUNCIÓN PRINCIPAL PARA TU LÓGICA
 * Aquí implementarás la lógica para modificar planillas en función a los tareos
 * 
 * @param {Object} empleado - Empleado activo con todos sus datos
 * @param {Object} contexto - Contexto adicional (metadatos, APIs, etc.)
 * @returns {Object} - Empleado procesado con modificaciones aplicadas
 */
async function aplicarLogicaPlanilla(empleado, contexto) {
  console.log('🔧 Aplicando lógica de planilla personalizada...');

  const feriadosPagoDoble = Array.isArray(empleado?.datosAPIs?.feriadosPagoDoble)
    ? empleado.datosAPIs.feriadosPagoDoble
    : [];
  console.log(`🎉 Feriados pago doble (datosAPIs) para ${empleado.empleado_nombre}: ${feriadosPagoDoble.length}`);

  // Viáticos standard: mismo tratamiento que grati (informan, no pagan)
  const viaticosCfg =
    contexto.contexto?.viaticos_standard
    || empleado.datosAPIs?.viaticos_standard
    || null;
  const plameIdViaticoDeclaracion =
    viaticosCfg?.activo && viaticosCfg?.plame_configuracion_id
      ? Number(viaticosCfg.plame_configuracion_id)
      : null;
  const esViaticoDeclaracionPorId = (plameId) =>
    plameIdViaticoDeclaracion != null
    && Number(plameId) === plameIdViaticoDeclaracion;
  const esViaticoDeclaracionPorCodigo = (codigo) => {
    const cod = String(codigo || '');
    if (cod === '0917') return true;
    return false;
  };
  const esViaticoDeclaracion = (plameId, codigo) =>
    esViaticoDeclaracionPorId(plameId) || esViaticoDeclaracionPorCodigo(codigo);
  
  // 📊 DATOS DISPONIBLES PARA TU LÓGICA:
  const datosDisponibles = {
    // 👤 DATOS DEL EMPLEADO
    empleadoId: empleado.empleado_id,
    nombre: empleado.empleado_nombre,
    posicion: empleado.empleado_posicion_activos,
    
    // 🏢 DATOS DE LA EMPRESA
    empresa: empleado.datosEmpresa,
    
    // 💰 DATOS SALARIALES COMPLETOS
    sueldo: empleado.datosSalariales.sueldoBasico,
    sistemaPensionario: empleado.datosSalariales.sistemaPensionario,
    afp: empleado.datosSalariales.afp,
    cuspp: empleado.datosSalariales.cuspp, // ✅ CUSPP disponible
    tipoAfp: empleado.datosSalariales.tipoAfp,
    regimenLaboral: empleado.datosSalariales.regimenLaboral,
    
    // 📋 HISTORIAL LABORAL COMPLETO (período que solapa con la planilla, no el primer registro)
    historialLaboral: (() => {
      const registros = empleado.datosEmpleado.historial_laboral || [];
      const empleadoHistorial = { historial_laboral: registros };
      const planilla = contexto.contexto?.planilla_real;
      const periodoEnPlanilla = planilla?.fecha_inicio && planilla?.fecha_fin
        ? obtenerPeriodoLaboralEnRango(empleadoHistorial, planilla.fecha_inicio, planilla.fecha_fin)
        : null;
      const historialOrdenado = [...registros]
        .sort((a, b) => new Date(b.fecha_ingreso) - new Date(a.fecha_ingreso));

      return {
        registros,
        totalRegistros: registros.length,
        ultimoHistorial: periodoEnPlanilla || historialOrdenado[0] || null,
        empleadoActivo: registros.some((h) => !h.fecha_cese),
        fechaIngresoActual: periodoEnPlanilla?.fecha_ingreso || historialOrdenado[0]?.fecha_ingreso || null,
        fechaCeseActual: periodoEnPlanilla?.fecha_cese || historialOrdenado[0]?.fecha_cese || null,
        fechasEfectivasEnPlanilla: planilla?.fecha_inicio && planilla?.fecha_fin
          ? obtenerFechasLaboralesEfectivasEnRango(empleadoHistorial, planilla.fecha_inicio, planilla.fecha_fin)
          : null,
      };
    })(),
    
    // 📋 CONCEPTOS ACTUALES
    conceptosIngresos: deduplicarConceptosPorPlame(empleado.conceptosUsuario.ingresos)
      .filter((c) => conceptoVaABoletaPlame(c)),
    conceptosDescuentos: deduplicarConceptosPorPlame(empleado.conceptosUsuario.descuentos)
      .filter((c) => conceptoVaABoletaPlame(c)),
    conceptosAportes: deduplicarConceptosPorPlame(empleado.conceptosUsuario.aportes),
    
    // ⏰ DATOS DE TAREOS DETALLADOS
    tareos: (() => {
      const tareosRaw = empleado.tareosEmpleadoFiltrados || empleado.tareosEmpleado || [];
      const tareosRawInterna = empleado.tareosEmpleadoInterna || tareosRaw;
      const mapaOverrides =
        empleado.overridesTareoEvento
        || contexto.contexto?.overrides_tareo_evento
        || contexto.arrayUnificado?.find((e) => e.tipo === 'contexto_empresarial')?.overrides_tareo_evento
        || {};
      const cfgTareo =
        empleado.datosAPIs?.configuracionTareo
        || contexto.contexto?.configuracion_tareo
        || null;
      const modoFijas8 = usaHorasFijas8Simple(cfgTareo);
      const tareosPlame = aplicarOverridesTareos(tareosRaw, mapaOverrides, 'plame', {
        empleadoId: empleado.empleado_id,
        modoFijas8,
      });
      const tareosInterna = aplicarOverridesTareos(tareosRawInterna, mapaOverrides, 'interna', {
        empleadoId: empleado.empleado_id,
        modoFijas8,
      });
      return {
        totalRegistros: empleado.resumenTareos?.total_tareos ?? tareosRaw.length,
        diasTrabajados: empleado.resumenTareos?.dias_trabajados,
        horasTotales: empleado.resumenTareos?.horas_totales,
        proyectos: empleado.resumenTareos?.proyectos,
        tareosPorTipo: empleado.resumenTareos?.tareos_por_tipo,
        // PLAME usa tareosDetallados con overrides plame
        tareosDetallados: tareosPlame,
        tareosDetalladosInterna: tareosInterna,
        tareosDetalladosOriginales: tareosRaw,
        overridesTareoEvento: mapaOverrides,
      };
    })(),
    
    // 🔐 DATOS DE AUTORIZACIONES
    autorizaciones: {
      totalRegistros: empleado.resumenAutorizaciones.total_autorizaciones,
      diasAutorizados: empleado.resumenAutorizaciones.total_dias_autorizados,
      horasAutorizadas: empleado.resumenAutorizaciones.total_horas_autorizadas,
      autorizacionesPorTipo: empleado.resumenAutorizaciones.autorizaciones_por_tipo,
      autorizacionesDetalladas: empleado.autorizacionesEmpleado // Array completo de autorizaciones
    },
    
    // ⏰ TAREOS DETALLADOS (array filtrado para cálculos)
    tareosDetallados: empleado.tareosEmpleadoFiltrados || empleado.tareosEmpleado,
    
    // ⚡ HORAS EXTRAS (calculadas desde autorizaciones)
    horasExtras: (() => {
      let horasExtra25 = 0;
      let horasExtra35 = 0;
      
      empleado.autorizacionesEmpleado.forEach(autorizacion => {
        if (autorizacion.sub_tipo === 'horas-extra-25') {
          horasExtra25 += parseFloat(autorizacion.horas || 0);
        } else if (autorizacion.sub_tipo === 'horas-extra-35') {
          horasExtra35 += parseFloat(autorizacion.horas || 0);
        }
      });
      
      return {
        horasExtra25: horasExtra25,
        horasExtra35: horasExtra35,
        totalHorasExtras: horasExtra25 + horasExtra35
      };
    })(),
    
    // 📊 APIS DISPONIBLES PARA CÁLCULOS
    apis: {
      afps: empleado.datosAPIs.afps,
      regimenesLaborales: empleado.datosAPIs.regimenesLaborales,
      conceptosPlame: empleado.datosAPIs.conceptosPlame,
      formulas: empleado.datosAPIs.formulas,
      tramosRenta: empleado.datosAPIs.tramosRenta,
      uit: empleado.datosAPIs.uit,
      sueldoMinimo: empleado.datosAPIs.sueldoMinimo,
      // ✅ CONCEPTOS AFECTOS A TAREOS
      conceptosAfectosTareos: empleado.datosAPIs.conceptosAfectosTareos,
      // ✅ INGRESOS QUE NO SE PRORRATEAN POR DÍAS DE VACACIONES (config. empresa)
      conceptosNoAfectanVacaciones: empleado.datosAPIs.conceptosNoAfectanVacaciones || [],
      // ✅ INGRESOS CUYO PROPORCIONAL DESCUENTA DÍAS DE LICENCIA CON GOCE (config. empresa)
      conceptosAfectanLicenciaGoceHaber: empleado.datosAPIs.conceptosAfectanLicenciaGoceHaber || [],
      // ✅ CONCEPTOS ESTÁTICOS CONFIGURADOS
      conceptosEstaticos: empleado.datosAPIs.conceptosEstaticos || [],
      // Feriados laborados con pago doble (fechas específicas por empresa)
      feriadosPagoDoble,
    },
    
    // 🔧 CONCEPTOS ESTÁTICOS DETALLADOS
    conceptosEstaticos: {
      completos: empleado.datosAPIs.conceptosEstaticos || [],
      ingresos: (empleado.datosAPIs.conceptosEstaticos || []).filter(concepto => 
        concepto.codigo && (concepto.codigo.startsWith('01') || concepto.codigo.startsWith('02') || 
                           concepto.codigo.startsWith('03') || concepto.codigo.startsWith('04') || 
                           concepto.codigo.startsWith('05') || concepto.codigo.startsWith('09') || 
                           concepto.codigo.startsWith('10') || concepto.codigo.startsWith('11') || 
                           concepto.codigo.startsWith('12') || concepto.codigo.startsWith('13'))
      ),
      descuentos: (empleado.datosAPIs.conceptosEstaticos || []).filter(concepto => 
        concepto.codigo && concepto.codigo.startsWith('07')
      ),
      aportes: (empleado.datosAPIs.conceptosEstaticos || []).filter(concepto => 
        concepto.codigo && (concepto.codigo.startsWith('06') || concepto.codigo.startsWith('08'))
      ),
      total: (empleado.datosAPIs.conceptosEstaticos || []).length
    },
    
    // 🏢 CONTEXTO GENERAL
    planilla: contexto.contexto?.planilla_real,
    empresa: contexto.contexto?.empresa,
    periodo: contexto.contexto?.periodo,
    
    // ⚙️ CONFIGURACIONES GENERALES
    configuracionesGenerales: {
      asignacionFamiliar: contexto.contexto?.configuraciones_generales?.asignacion_familiar || false,
      timestamp: contexto.contexto?.configuraciones_generales?.timestamp,
      aplicadoPorUsuario: contexto.contexto?.configuraciones_generales?.aplicado_por_usuario || false
    },
    
    // 📅 ANÁLISIS DE MESES Y DÍAS DE LA PLANILLA
    analisisPeriodo: (() => {
      const planilla = contexto.contexto?.planilla_real;
      if (!planilla || !planilla.fecha_inicio || !planilla.fecha_fin) {
        return null;
      }
      
      // ✅ EXTRAER FECHAS COMO TEXTO para evitar problemas de zona horaria
      // Formato esperado: "2025-06-01T00:00:00.000000Z" -> extraer "2025-06-01"
      const fechaInicioTexto = planilla.fecha_inicio.split('T')[0]; // "2025-06-01"
      const fechaFinTexto = planilla.fecha_fin.split('T')[0]; // "2025-06-30"
      
      // Extraer año, mes y día como números
      const [añoInicio, mesInicioStr, diaInicio] = fechaInicioTexto.split('-');
      const [añoFin, mesFinStr, diaFin] = fechaFinTexto.split('-');
      
      const mesInicio = parseInt(mesInicioStr) - 1; // Convertir a 0-11 para consistencia
      const mesFin = parseInt(mesFinStr) - 1; // Convertir a 0-11 para consistencia
      
      console.log('🔍 DEBUG FECHAS COMO TEXTO:');
      console.log('  planilla.fecha_inicio (original):', planilla.fecha_inicio);
      console.log('  planilla.fecha_fin (original):', planilla.fecha_fin);
      console.log('  fechaInicioTexto:', fechaInicioTexto);
      console.log('  fechaFinTexto:', fechaFinTexto);
      console.log('  añoInicio:', añoInicio, 'mesInicio:', mesInicio + 1, 'diaInicio:', diaInicio);
      console.log('  añoFin:', añoFin, 'mesFin:', mesFin + 1, 'diaFin:', diaFin);
      
      // Crear objetos Date usando la zona horaria local para cálculos de días
      const fechaInicio = new Date(parseInt(añoInicio), mesInicio, parseInt(diaInicio));
      const fechaFin = new Date(parseInt(añoFin), mesFin, parseInt(diaFin));
      
      console.log('  fechaInicio (Date local):', fechaInicio);
      console.log('  fechaFin (Date local):', fechaFin);
      
      // Información básica del período
      const totalDias = Math.ceil((fechaFin - fechaInicio) / (1000 * 60 * 60 * 24)) + 1;
      
      // Verificar si abarca uno o dos meses usando los valores extraídos como texto
      console.log('🔍 DEBUG COMPARACIÓN:');
      console.log('  mesInicio:', mesInicio, '(mes', mesInicio + 1, ')');
      console.log('  mesFin:', mesFin, '(mes', mesFin + 1, ')');
      console.log('  añoInicio:', añoInicio);
      console.log('  añoFin:', añoFin);
      console.log('  mesInicio !== mesFin:', mesInicio !== mesFin);
      console.log('  añoInicio !== añoFin:', parseInt(añoInicio) !== parseInt(añoFin));
      
      const abarcaDosMeses = (mesInicio !== mesFin) || (parseInt(añoInicio) !== parseInt(añoFin));
      
      console.log('🔍 RESULTADO: abarcaDosMeses =', abarcaDosMeses);
    console.log('✅ PROBLEMA DE ZONA HORARIA SOLUCIONADO - usando fechas como texto');
      
             if (!abarcaDosMeses) {
         // UN SOLO MES
         const diasCompletosDelMes = 30; // ✅ FORZADO A 30 DÍAS SIEMPRE (antes calculaba 28/29/30/31)
         
         return {
           tipo: 'mes_unico',
           totalDias: totalDias,
           mes: {
             numero: mesInicio + 1, // 1-12
             nombre: fechaInicio.toLocaleDateString('es-ES', { month: 'long' }),
             año: parseInt(añoInicio),
            diasEnMes: totalDias, // Días de la planilla en este mes
            diasCompletosDelMes: diasCompletosDelMes // ✅ Base legal: 30 días fijos para cálculos
           },
           fechaInicio: planilla.fecha_inicio,
           fechaFin: planilla.fecha_fin,
           detallesDias: {
             [`${añoInicio}-${(mesInicio + 1).toString().padStart(2, '0')}`]: totalDias
           },
           diasTotalesPorMes: {
             [`${añoInicio}-${(mesInicio + 1).toString().padStart(2, '0')}`]: diasCompletosDelMes
           }
         };
             } else {
         // DOS MESES
         // Calcular último día del primer mes
         const ultimoDiaPrimerMes = new Date(parseInt(añoInicio), mesInicio + 1, 0);
         
         // Días en el primer mes (desde fecha inicio hasta fin de mes)
         const diasPrimerMes = Math.ceil((ultimoDiaPrimerMes - fechaInicio) / (1000 * 60 * 60 * 24)) + 1;
         
         // Días en el segundo mes (desde inicio de mes hasta fecha fin)
         const diasSegundoMes = totalDias - diasPrimerMes;
         
        // Días completos de cada mes
        const diasCompletosPrimerMes = 30; // ✅ FORZADO A 30 DÍAS SIEMPRE (antes calculaba 28/29/30/31)
        const diasCompletosSegundoMes = 30; // ✅ FORZADO A 30 DÍAS SIEMPRE (antes calculaba 28/29/30/31)
         
         return {
           tipo: 'dos_meses',
           totalDias: totalDias,
           primerMes: {
             numero: mesInicio + 1,
             nombre: fechaInicio.toLocaleDateString('es-ES', { month: 'long' }),
            año: parseInt(añoInicio),
            diasEnMes: diasPrimerMes, // Días de la planilla en este mes
            diasCompletosDelMes: diasCompletosPrimerMes, // ✅ Base legal: 30 días fijos para cálculos
            fechaInicio: planilla.fecha_inicio,
            fechaFin: `${añoInicio}-${(mesInicio + 1).toString().padStart(2, '0')}-${ultimoDiaPrimerMes.getDate().toString().padStart(2, '0')}`
          },
          segundoMes: {
            numero: mesFin + 1,
            nombre: fechaFin.toLocaleDateString('es-ES', { month: 'long' }),
            año: parseInt(añoFin),
            diasEnMes: diasSegundoMes, // Días de la planilla en este mes
            diasCompletosDelMes: diasCompletosSegundoMes, // ✅ Base legal: 30 días fijos para cálculos
             fechaInicio: `${añoFin}-${(mesFin + 1).toString().padStart(2, '0')}-01`,
             fechaFin: planilla.fecha_fin
           },
           fechaInicio: planilla.fecha_inicio,
           fechaFin: planilla.fecha_fin,
           detallesDias: {
             [`${añoInicio}-${(mesInicio + 1).toString().padStart(2, '0')}`]: diasPrimerMes,
             [`${añoFin}-${(mesFin + 1).toString().padStart(2, '0')}`]: diasSegundoMes
           },
           diasTotalesPorMes: {
             [`${añoInicio}-${(mesInicio + 1).toString().padStart(2, '0')}`]: diasCompletosPrimerMes,
             [`${añoFin}-${(mesFin + 1).toString().padStart(2, '0')}`]: diasCompletosSegundoMes
           }
         };
      }
    })(),

    // 💤 DESCANSOS SEMANALES PROPORCIONALES
    descansosSemanales: {
      configuracionActivada: empleado.datosAPIs?.configuracionTareo?.descontar_descansos || false,
      semanasCalculadas: empleado.resumenTareos?.descansos_semanales || [],
      totalSemanas: (empleado.resumenTareos?.descansos_semanales || []).length,
      resumenGeneral: (() => {
        const semanas = empleado.resumenTareos?.descansos_semanales || [];
        if (semanas.length === 0) {
          return {
            totalHorasDescanso: 0,
            totalFaltas: 0,
            totalDiasLaborables: 0,
            promedioDescanso: 0
          };
        }

        const totalHorasDescanso = semanas.reduce((sum, s) => sum + s.descansoProporcionado, 0);
        const totalFaltas = semanas.reduce((sum, s) => sum + s.faltas, 0);
        const totalDiasLaborables = semanas.reduce((sum, s) => sum + s.diasLaborables, 0);
        const promedioDescanso = semanas.length > 0 ? (totalHorasDescanso / semanas.length) : 0;

        return {
          totalHorasDescanso,
          totalFaltas,
          totalDiasLaborables,
          promedioDescanso
        };
      })()
    },

    // 💤 COMPENSACIONES DE MESES PASADOS
    compensacionesMesesPasados: (() => {
      const seccionCompensaciones = contexto.arrayUnificado?.find(elem => elem.tipo === 'compensaciones_meses_pasados');
      const compensacionEmpleado = seccionCompensaciones?.resumen_por_empleado?.[empleado.empleado_id];
      
      return {
        disponibles: !!seccionCompensaciones,
        totalCompensacionesSistema: seccionCompensaciones?.total_compensaciones || 0,
        totalHorasSistema: seccionCompensaciones?.total_horas_compensadas || 0,
        compensacionesActivasSistema: seccionCompensaciones?.compensaciones_activas || 0,
        empleadoTieneCompensacion: !!compensacionEmpleado,
        horasEmpleado: compensacionEmpleado?.horas_compensadas || 0,
        compensacionId: compensacionEmpleado?.compensacion_id || null,
        metadatos: seccionCompensaciones?.metadatos || null
             };
     })()
  };

  // Cambio de sueldo a partir de un día (Consideraciones Previas): prorratea 0121 y mueve extras/AFP/ONP
  const mapaSueldoTramos =
    empleado.tramosSueldoPlame
    || contexto.contexto?.tramos_sueldo_plame
    || contexto.arrayUnificado?.find((e) => e.tipo === 'contexto_empresarial')?.tramos_sueldo_plame
    || {};
  const cfgSueldoTramo = tramoSueldoPlameActivo(mapaSueldoTramos, empleado.empleado_id);
  if (cfgSueldoTramo && destinoAplicaPlame(cfgSueldoTramo)) {
    const aplicado = aplicarTramoSueldoAIngresos({
      conceptosIngresos: datosDisponibles.conceptosIngresos,
      config: cfgSueldoTramo,
      analisisPeriodo: datosDisponibles.analisisPeriodo,
      historialLaboral: datosDisponibles.historialLaboral,
    });
    datosDisponibles.conceptosIngresos = aplicado.conceptosIngresos;
    datosDisponibles.sueldo = aplicado.sueldoEfectivo;
    if (empleado.datosSalariales) {
      empleado.datosSalariales.sueldoBasico = aplicado.sueldoEfectivo;
    }
    if (empleado.conceptosUsuario) {
      empleado.conceptosUsuario.ingresos = aplicado.conceptosIngresos;
    }
    console.log('💰 Cambio de sueldo por día aplicado:', {
      empleado: empleado.empleado_nombre,
      fecha_cambio: aplicado.fechaCambio,
      dias1: aplicado.dias1,
      dias2: aplicado.dias2,
      monto0121: aplicado.monto0121,
    });
  }
  
  
     console.log('🔍 Datos disponibles para lógica:', datosDisponibles);
     
     // ✅ VERIFICAR DISPONIBILIDAD DE CONCEPTOS ESTÁTICOS
     //console.log('\n🔧 === VERIFICACIÓN CONCEPTOS ESTÁTICOS ===');
     //console.log(`🔧 Conceptos estáticos en APIs: ${datosDisponibles.apis.conceptosEstaticos.length} registros`);
     //console.log(`🔧 Conceptos estáticos detallados: ${datosDisponibles.conceptosEstaticos.total} registros`);
     if (datosDisponibles.conceptosEstaticos.total > 0) {
       //console.log(`  📈 Ingresos estáticos: ${datosDisponibles.conceptosEstaticos.ingresos.length}`);
       //console.log(`  📉 Descuentos estáticos: ${datosDisponibles.conceptosEstaticos.descuentos.length}`);
       //console.log(`  🏛️ Aportes estáticos: ${datosDisponibles.conceptosEstaticos.aportes.length}`);
       //console.log('✅ Conceptos estáticos disponibles para procesamiento');
     } else {
       //console.log('⚠️ Sin conceptos estáticos configurados - solo se procesarán conceptos regulares');
     }

    
   
   // 📋 MOSTRAR INFORMACIÓN DEL HISTORIAL LABORAL
   if (datosDisponibles.historialLaboral && datosDisponibles.historialLaboral.totalRegistros > 0) {
     //console.log('\n📋 === HISTORIAL LABORAL DEL EMPLEADO ===');
     //console.log(`📊 Total de registros: ${datosDisponibles.historialLaboral.totalRegistros}`);
     //console.log(`✅ Empleado activo (sin fecha cese): ${datosDisponibles.historialLaboral.empleadoActivo ? 'SÍ' : 'NO'}`);
     //console.log(`📅 Fecha ingreso actual: ${datosDisponibles.historialLaboral.fechaIngresoActual || 'No disponible'}`);
     //console.log(`📅 Fecha cese actual: ${datosDisponibles.historialLaboral.fechaCeseActual || 'Sin cese (activo)'}`);
     
     if (datosDisponibles.historialLaboral.ultimoHistorial) {
       //console.log('\n📄 ÚLTIMO REGISTRO DEL HISTORIAL:');
       //console.log(`  - ID: ${datosDisponibles.historialLaboral.ultimoHistorial.id}`);
       //console.log(`  - Fecha Ingreso: ${datosDisponibles.historialLaboral.ultimoHistorial.fecha_ingreso}`);
       //console.log(`  - Fecha Cese: ${datosDisponibles.historialLaboral.ultimoHistorial.fecha_cese || 'Sin cese'}`);
       //console.log(`  - Motivo Cese: ${datosDisponibles.historialLaboral.ultimoHistorial.motivo_cese || 'N/A'}`);
     }
     
     if (datosDisponibles.historialLaboral.totalRegistros > 1) {
       //console.log('\n📚 TODOS LOS REGISTROS DEL HISTORIAL:');
       datosDisponibles.historialLaboral.registros
         .sort((a, b) => new Date(b.fecha_ingreso) - new Date(a.fecha_ingreso))
         .forEach((registro, index) => {
           console.log(`  ${index + 1}. Ingreso: ${registro.fecha_ingreso} | Cese: ${registro.fecha_cese || 'Activo'} | Motivo: ${registro.motivo_cese || 'N/A'}`);
         });
     }
   } else {
     //console.log('\n⚠️ === SIN HISTORIAL LABORAL DISPONIBLE ===');
   }
   
   // ⚙️ MOSTRAR CONFIGURACIONES GENERALES
   //console.log('\n⚙️ === CONFIGURACIONES GENERALES ===');
   //console.log(`👶 Asignación familiar: ${datosDisponibles.configuracionesGenerales.asignacionFamiliar ? 'ACTIVADA' : 'DESACTIVADA'}`);
   //console.log(`🕒 Timestamp: ${datosDisponibles.configuracionesGenerales.timestamp || 'No disponible'}`);
   //console.log(`👤 Aplicado por usuario: ${datosDisponibles.configuracionesGenerales.aplicadoPorUsuario ? 'SÍ' : 'NO'}`);
   
   // 📅 MOSTRAR ANÁLISIS DEL PERÍODO
   if (datosDisponibles.analisisPeriodo) {
     //console.log('\n📅 === ANÁLISIS DEL PERÍODO DE LA PLANILLA ===');
     const analisis = datosDisponibles.analisisPeriodo;
     
     if (analisis.tipo === 'mes_unico') {
       //console.log('🗓️ Tipo: UN SOLO MES');
       //console.log(`📅 Mes: ${analisis.mes.nombre} ${analisis.mes.año} (Mes ${analisis.mes.numero})`);
       //console.log(`📊 Días de planilla: ${analisis.totalDias}`);
       //console.log(`🗓️ Días completos del mes: ${analisis.mes.diasCompletosDelMes}`);
       //console.log(`📍 Período: ${analisis.fechaInicio} a ${analisis.fechaFin}`);
     } else if (analisis.tipo === 'dos_meses') {
       //console.log('🗓️ Tipo: DOS MESES');
       //console.log(`📅 Primer mes: ${analisis.primerMes.nombre} ${analisis.primerMes.año}`);
       //console.log(`   📊 Días de planilla: ${analisis.primerMes.diasEnMes} | 🗓️ Días completos del mes: ${analisis.primerMes.diasCompletosDelMes}`);
       //console.log(`📅 Segundo mes: ${analisis.segundoMes.nombre} ${analisis.segundoMes.año}`);
       //console.log(`   📊 Días de planilla: ${analisis.segundoMes.diasEnMes} | 🗓️ Días completos del mes: ${analisis.segundoMes.diasCompletosDelMes}`);
       //console.log(`📊 Total días planilla: ${analisis.totalDias}`);
       //console.log(`📍 Período completo: ${analisis.fechaInicio} a ${analisis.fechaFin}`);
       //console.log('🗓️ Días totales por mes:', analisis.diasTotalesPorMes);
     }
   } else {
     //console.log('⚠️ No se pudo analizar el período de la planilla');
   }

   // 💤 MOSTRAR INFORMACIÓN DE DESCANSOS SEMANALES
   //console.log('\n💤 === DESCANSOS SEMANALES PROPORCIONALES ===');
   //console.log(`⚙️ Configuración descontar_descansos: ${datosDisponibles.descansosSemanales.configuracionActivada ? 'ACTIVADO' : 'DESACTIVADO'}`);
   //console.log(`📊 Total semanas calculadas: ${datosDisponibles.descansosSemanales.totalSemanas}`);
   
   if (datosDisponibles.descansosSemanales.totalSemanas > 0) {
     const resumen = datosDisponibles.descansosSemanales.resumenGeneral;
     //console.log(`⏰ Total horas de descanso: ${resumen.totalHorasDescanso.toFixed(2)}h`);
     //console.log(`❌ Total faltas: ${resumen.totalFaltas}`);
     //console.log(`📅 Total días laborables: ${resumen.totalDiasLaborables}`);
     //console.log(`📊 Promedio descanso por semana: ${resumen.promedioDescanso.toFixed(2)}h`);
     
     //console.log('📋 Detalle por semana:');
     
     let totalHorasDescuento = 0; // ✅ DEFINIR LA VARIABLE ANTES DE USARLA
     datosDisponibles.descansosSemanales.semanasCalculadas.forEach((semana, index) => {
       // ✅ SOLO PROCESAR SEMANAS CON FECHAS DIFERENTES (NO MISMAS FECHAS)
       if (semana.fechaInicio !== semana.fechaFin) {
         const descansoCompletoSemana = 8; // 8 horas de descanso completo por semana
         const horasDescuentoSemana = descansoCompletoSemana - semana.descansoProporcionado;
         totalHorasDescuento += horasDescuentoSemana;
         
         console.log(`  ${index + 1}. ${semana.fechaInicio} - ${semana.fechaFin}:`);
         console.log(`     Fechas: ${semana.fechaInicio} vs ${semana.fechaFin} (✅ PROCESANDO - fechas diferentes)`);
         console.log(`     Descanso completo: ${descansoCompletoSemana}h`);
         console.log(`     Descanso proporcionado: ${semana.descansoProporcionado.toFixed(2)}h`);
         console.log(`     Horas a descontar: ${horasDescuentoSemana.toFixed(2)}h`);
       } else {
         console.log(`  ${index + 1}. ${semana.fechaInicio} - ${semana.fechaFin}:`);
         console.log(`     Fechas: ${semana.fechaInicio} vs ${semana.fechaFin} (❌ IGNORANDO - misma fecha)`);
       }
     });
   } else {
     //console.log('⚠️ No hay descansos semanales calculados');
   }
   
   // 📅 MOSTRAR INFORMACIÓN DE COMPENSACIONES DE MESES PASADOS
   //console.log('\n📅 === COMPENSACIONES DE MESES PASADOS ===');
   const compensaciones = datosDisponibles.compensacionesMesesPasados;
   //console.log(`📋 Compensaciones disponibles en sistema: ${compensaciones.disponibles ? 'SÍ' : 'NO'}`);
   
   if (compensaciones.disponibles) {
     //console.log(`📊 Total compensaciones en planilla: ${compensaciones.totalCompensacionesSistema}`);
     //console.log(`⏰ Total horas en planilla: ${compensaciones.totalHorasSistema}h`);
     //console.log(`✅ Compensaciones activas en planilla: ${compensaciones.compensacionesActivasSistema}`);
     //console.log(`👤 Empleado tiene compensación: ${compensaciones.empleadoTieneCompensacion ? 'SÍ' : 'NO'}`);
     
     if (compensaciones.empleadoTieneCompensacion) {
       //console.log(`💰 Horas del empleado: ${compensaciones.horasEmpleado}h`);
       //console.log(`🆔 ID Compensación: ${compensaciones.compensacionId}`);
     } else {
       //console.log(`💰 Horas del empleado: 0h (sin compensaciones)`);
     }
   } else {
     //console.log('⚠️ No hay compensaciones de meses pasados disponibles');
   }
   
   // 🔧 MOSTRAR CONCEPTOS ESTÁTICOS DISPONIBLES
   //console.log('\n🔧 === CONCEPTOS ESTÁTICOS CONFIGURADOS ===');
   //console.log(`📊 Total conceptos estáticos: ${datosDisponibles.conceptosEstaticos.total}`);
   if (datosDisponibles.conceptosEstaticos.total > 0) {
     //console.log(`📈 Conceptos de ingresos: ${datosDisponibles.conceptosEstaticos.ingresos.length}`);
     //console.log(`📉 Conceptos de descuentos: ${datosDisponibles.conceptosEstaticos.descuentos.length}`);
     //console.log(`🏛️ Conceptos de aportes: ${datosDisponibles.conceptosEstaticos.aportes.length}`);
     
     // Mostrar algunos ejemplos si hay conceptos
     if (datosDisponibles.conceptosEstaticos.completos.length > 0) {
       //console.log('\n🔍 Ejemplos de conceptos estáticos configurados:');
       datosDisponibles.conceptosEstaticos.completos.slice(0, 5).forEach((concepto, index) => {
         //console.log(`  ${index + 1}. [${concepto.codigo || 'Sin código'}] ${concepto.nombre || concepto.descripcion || `ID ${concepto.id}`}`);
       });
       
       if (datosDisponibles.conceptosEstaticos.completos.length > 5) {
         //console.log(`  ... y ${datosDisponibles.conceptosEstaticos.completos.length - 5} conceptos más`);
       }
     }
   } else {
     //console.log('⚠️ No hay conceptos estáticos configurados para esta empresa');
   }
  // 📅 FUNCIÓN HELPER: CALCULAR DÍAS EFECTIVOS CONSIDERANDO INGRESO Y CESE DEL EMPLEADO
  const calcularDiasEfectivos = (analisisPeriodo, historialLaboral) => {
    
    //console.log('\n📅 === CALCULANDO DÍAS EFECTIVOS (INGRESO + CESE) ===');  
    if (!analisisPeriodo) {
      //console.log('⚠️ No hay análisis de período disponible');
      return 0;
    }
    
    // ✅ EXTRAER FECHAS COMO TEXTO para evitar problemas de zona horaria
    const fechaInicioPlanillaTexto = analisisPeriodo.fechaInicio.split('T')[0];
    const fechaFinPlanillaTexto = analisisPeriodo.fechaFin.split('T')[0];
    const fechaIngresoEmpleadoTexto = historialLaboral.fechaIngresoActual.split('T')[0];
    
    // Crear objetos Date con zona horaria local para comparaciones
    const [añoIniPlanilla, mesIniPlanilla, diaIniPlanilla] = fechaInicioPlanillaTexto.split('-');
    const [añoFinPlanilla, mesFinPlanilla, diaFinPlanilla] = fechaFinPlanillaTexto.split('-');
    const [añoIngresoEmp, mesIngresoEmp, diaIngresoEmp] = fechaIngresoEmpleadoTexto.split('-');
    
    const fechaInicioPlanilla = new Date(parseInt(añoIniPlanilla), parseInt(mesIniPlanilla) - 1, parseInt(diaIniPlanilla));
    const fechaFinPlanilla = new Date(parseInt(añoFinPlanilla), parseInt(mesFinPlanilla) - 1, parseInt(diaFinPlanilla));
    const fechaIngresoEmpleado = new Date(parseInt(añoIngresoEmp), parseInt(mesIngresoEmp) - 1, parseInt(diaIngresoEmp));
    
    //console.log('📍 Período planilla:', fechaInicioPlanillaTexto, 'a', fechaFinPlanillaTexto);
    //console.log('📅 Fecha ingreso empleado:', fechaIngresoEmpleadoTexto);
    //console.log('📅 Fecha cese empleado:', historialLaboral.fechaCeseActual || 'Activo (sin cese)');
    
    // Determinar fecha de inicio efectiva (la más tardía entre ingreso de empleado e inicio de planilla)
    const fechaInicioEfectiva = fechaIngresoEmpleado > fechaInicioPlanilla ? fechaIngresoEmpleado : fechaInicioPlanilla;
    
    // Determinar fecha de fin efectiva
    let fechaFinEfectiva = fechaFinPlanilla;
    
    if (historialLaboral.fechaCeseActual) {
      const fechaCeseEmpleadoTexto = historialLaboral.fechaCeseActual.split('T')[0];
      const [añoCeseEmp, mesCeseEmp, diaCeseEmp] = fechaCeseEmpleadoTexto.split('-');
      const fechaCeseEmpleado = new Date(parseInt(añoCeseEmp), parseInt(mesCeseEmp) - 1, parseInt(diaCeseEmp));
      
      // Si fue cesado antes del fin de la planilla, usar fecha de cese
      if (fechaCeseEmpleado < fechaFinPlanilla) {
        fechaFinEfectiva = fechaCeseEmpleado;
      }
    }
    
    //console.log('🎯 Fecha inicio efectiva:', fechaInicioEfectiva.toISOString().split('T')[0]);
    //console.log('🎯 Fecha fin efectiva:', fechaFinEfectiva.toISOString().split('T')[0]);
    
    // Validaciones de coherencia
    if (fechaIngresoEmpleado > fechaFinPlanilla) {
      //console.log('⚠️ Empleado ingresó DESPUÉS del período de planilla - no debería estar incluido');
      return 0;
    }
    
    if (historialLaboral.fechaCeseActual) {
      const fechaCeseEmpleadoTexto = historialLaboral.fechaCeseActual.split('T')[0];
      const [añoCeseEmp, mesCeseEmp, diaCeseEmp] = fechaCeseEmpleadoTexto.split('-');
      const fechaCeseEmpleado = new Date(parseInt(añoCeseEmp), parseInt(mesCeseEmp) - 1, parseInt(diaCeseEmp));
      
      if (fechaCeseEmpleado < fechaInicioPlanilla) {
        //console.log('⚠️ Empleado cesado ANTES del período de planilla - no debería estar incluido');
        return 0;
      }
    }
    
    // Calcular días efectivos entre fecha inicio efectiva y fecha fin efectiva
    const diasEfectivos = Math.ceil((fechaFinEfectiva - fechaInicioEfectiva) / (1000 * 60 * 60 * 24)) + 1;
    
    // Análisis detallado para debug
    const esIngresoTardio = fechaIngresoEmpleado > fechaInicioPlanilla;
    let esCeseTemprano = false;
    
    if (historialLaboral.fechaCeseActual) {
      const fechaCeseEmpleadoTexto = historialLaboral.fechaCeseActual.split('T')[0];
      const [añoCeseEmp, mesCeseEmp, diaCeseEmp] = fechaCeseEmpleadoTexto.split('-');
      const fechaCeseEmpleado = new Date(parseInt(añoCeseEmp), parseInt(mesCeseEmp) - 1, parseInt(diaCeseEmp));
      esCeseTemprano = fechaCeseEmpleado < fechaFinPlanilla;
    }
    
    //console.log('📊 ANÁLISIS DE DÍAS EFECTIVOS:');
    //console.log(`  🔸 Ingreso tardío: ${esIngresoTardio ? 'SÍ' : 'NO'} ${esIngresoTardio ? '(ingresó durante el período)' : ''}`);
    //console.log(`  🔸 Cese temprano: ${esCeseTemprano ? 'SÍ' : 'NO'} ${esCeseTemprano ? '(cesado durante el período)' : ''}`);
    //console.log(`  🔸 Días totales período: ${analisisPeriodo.totalDias}`);
    //console.log(`  🔸 Días efectivos empleado: ${diasEfectivos}`);
    //console.log(`  🔸 Diferencia: ${analisisPeriodo.totalDias - diasEfectivos} días ${analisisPeriodo.totalDias > diasEfectivos ? 'reducidos' : 'agregados'}`);
    
    return Math.max(0, diasEfectivos); // Asegurar que no sea negativo
  };

  // Primero saco los conceptos de ingresos y los comparo con los conceptos de tareos
  const conceptosAfectosTareos = datosDisponibles.apis.conceptosAfectosTareos;
  const filasNoAfectanVacaciones = Array.isArray(datosDisponibles.apis.conceptosNoAfectanVacaciones)
    ? datosDisponibles.apis.conceptosNoAfectanVacaciones
    : [];
  const ingresoNoAfectaVacaciones = (plameConfigId) =>
    filasNoAfectanVacaciones.some(
      (row) => Number(row.id_concepto_plame) === Number(plameConfigId)
    );
  const filasAfectanLicenciaGoce = Array.isArray(datosDisponibles.apis.conceptosAfectanLicenciaGoceHaber)
    ? datosDisponibles.apis.conceptosAfectanLicenciaGoceHaber
    : [];
  const ingresoDescuentaDiasLicenciaGoce = (plameConfigId) =>
    filasAfectanLicenciaGoce.some(
      (row) => Number(row.id_concepto_plame) === Number(plameConfigId)
    );
  const datosTareosDetalle = datosDisponibles.tareos.tareosDetallados;
  const diasLicenciaGoceEnPeriodo = datosTareosDetalle.filter(
    (t) => t.tipo_evento === 'licencia_con_goce_de_haber'
  ).length;
  // Configuración: solo esos códigos PLAME (id = plame_configuracion) descuentan días al proporcional
  console.log('🧾 [LIC. GOCE HABER] Códigos en configuración (empresa) — se compara con cada ingreso:', filasAfectanLicenciaGoce.length
    ? filasAfectanLicenciaGoce.map((r) => ({
        id_plame: r.id_concepto_plame,
        codigo: r.codigo,
        descripcion: r.descripcion || r.nombre
      }))
    : '(ninguno; nadie se descuenta días de lic. con goce)');
  console.log(
    `🧾 [LIC. GOCE HABER] Días tareo "licencia_con_goce_de_haber" en el período (este empleado): ${diasLicenciaGoceEnPeriodo}. ` +
    '(Solo tareo con goce; no hay tipo tareo "sin goce" en el sistema).'
  );
  // Estático: monto de ficha. En agrario solo 0201 (asignación familiar) se calcula/prorratea.
  const esRegimenAgrario = esRegimenAgrarioDeEmpleado({
    codigo: datosDisponibles.regimenLaboral,
    regimenSeleccionado: empleado.datosAPIs?.regimenSeleccionado,
  });
  const conceptosIngresos = datosDisponibles.conceptosIngresos;
  const listaIngresos = [];
  console.log('🔍 Conceptos ingresos:', conceptosIngresos);
  // ahora comparo los conceptos afectos a tareos con los conceptos ingresos
  const IngresosConceptos = conceptosIngresos.forEach(concepto => {
    const conceptoAfectoId = concepto.plame_configuracion.id;
    //console.log('🔍 Id de ingresos:', conceptoAfectoId);
    //console.log('lista de conceptos afectos a tareos:', conceptosAfectosTareos);
    
    // ahora averiguo si el concepto id de ingresos esta en la lista de conceptos afectos a tareos
    const conceptoAfecto = conceptosAfectosTareos.find(correlacion => correlacion.id_concepto_plame === conceptoAfectoId) ? 1:0;
    console.log('🔍 Concepto afecto a tareos:', conceptoAfecto);
    const Ingreso = {concepto: concepto, conceptoAfecto: conceptoAfecto};
    listaIngresos.push(Ingreso);
  });
  console.log('🔍 Lista de ingresos afectos:', listaIngresos);
  const Ingresos = [];
  /** Campos vacíos de lic. con goce (mismo shape en todo el array de ingresos) */
  const ingresoSinDescuentoLicGoceHaber = () => ({
    en_config_licencia_goce_haber: false,
    dias_licencia_goce_haber: 0,
    monto_descuento_licencia_goce_haber: 0
  });
  const tasaNocturna = empleado.tasaNocturna;
  const tasaNocturnaPrioriza0309 = tasaNocturnaPriorizaSobreConceptoEstatico(tasaNocturna);
  const configTasaNocturnaApi = empleado.datosAPIs?.configuracionTasaNocturna ?? null;
  const concepto0309Plantilla = (conceptosIngresos || []).find(
    (c) => String(c.plame_configuracion?.codigo_plame) === CODIGO_PLAME_BONIFICACION_NOCTURNA
  );
  const es0309EstaticoEmpresa = Boolean(
    datosDisponibles.conceptosEstaticos?.completos?.some(
      (c) => String(c.codigo) === CODIGO_PLAME_BONIFICACION_NOCTURNA
    )
  );
  const tareosConFlagNocturno = (datosDisponibles.tareos?.tareosDetallados || []).filter(
    (t) => t.considerar_tasa_nocturna === true || t.considerar_tasa_nocturna === 1
      || String(t.considerar_tasa_nocturna || '').toLowerCase() === 'si'
      || String(t.considerar_tasa_nocturna || '').toLowerCase() === 's'
  );

  logDiagnosticoBonificacionNocturna('Inicio cálculo empleado', {
    empleado_id: empleado.empleado_id,
    empleado_nombre: empleado.empleado_nombre,
    config_api: configTasaNocturnaApi,
    tasaNocturna_armada: tasaNocturna,
    tasaNocturnaPrioriza0309,
    es0309_en_conceptos_estaticos_empresa: es0309EstaticoEmpresa,
    concepto0309_plantilla_monto: concepto0309Plantilla?.monto ?? null,
    tareos_con_flag_nocturno: tareosConFlagNocturno.length,
    fechas_tareos_nocturnos: tareosConFlagNocturno.map((t) => t.fecha),
  });
  const calculosIngresos = listaIngresos.forEach(ingreso => {
      const codigoPlameIngreso = ingreso.concepto.plame_configuracion.codigo_plame;
      const plameIdPlantilla = ingreso.concepto.plame_configuracion?.id;
      // Viáticos standard solo entran por tareo (con destino plame/interna/ambas), no por plantilla del empleado
      if (esViaticoDeclaracion(plameIdPlantilla, codigoPlameIngreso)) {
        console.log(
          `🧳 Viático ${codigoPlameIngreso} omitido de plantilla PLAME: se carga solo desde tareo según destino`
        );
        return;
      }
      if (debeOmitir0309EnProcesamientoPlantilla(codigoPlameIngreso, tasaNocturna)) {
        console.log(
          `🌙 ${CODIGO_PLAME_BONIFICACION_NOCTURNA} omitido del proporcional/estático: tasa nocturna activa en planilla`
        );
        return;
      }
      console.log('🔍 Ingreso afecto a tareos:', ingreso.concepto);
      console.log('Contar para los dias Tareos', datosDisponibles.tareos.totalRegistros);
      // Calculo de dias trabajdos para mes unido y dos meses
              if (datosDisponibles.analisisPeriodo.tipo === 'mes_unico') {
          console.log('🔍 Mes único');
          console.log('🔍 Días trabajados:', datosDisponibles.analisisPeriodo.detallesDias);
          
          // Calcular días efectivos considerando cese del empleado
          const diasMaximosPeriodoCalculados = calcularDiasEfectivos(datosDisponibles.analisisPeriodo, datosDisponibles.historialLaboral);
          // ✅ LIMITAR A MÁXIMO 30 DÍAS (ya no 30 o 31, siempre 30)
          const diasMaximosPeriodo = Math.min(30, diasMaximosPeriodoCalculados);
          
          const exclVac = ingresoNoAfectaVacaciones(ingreso.concepto.plame_configuracion.id);

          // Contar días especiales para restar del cálculo del sueldo básico (SOLO DEL PERÍODO)
          const diasEspecialesDescuento = datosTareosDetalle.filter(tareo => 
            ['vacaciones'].includes(tareo.tipo_evento)
          ).length;
          const diasEspecialesDescuentoAplicado = exclVac ? 0 : diasEspecialesDescuento;
          const aplicaDescuentoDiasLicGoce = ingresoDescuentaDiasLicenciaGoce(ingreso.concepto.plame_configuracion.id);
          const diasLicenciaGoceAplicado = aplicaDescuentoDiasLicGoce ? diasLicenciaGoceEnPeriodo : 0;
          const diasBaseMes = datosDisponibles.analisisPeriodo.mes.diasCompletosDelMes;
          const montoConcepto = parseFloat(ingreso.concepto.monto) || 0;
          const montoPorDia = diasBaseMes > 0 ? montoConcepto / diasBaseMes : 0;
          // Parte de proporcional “asociable” a esos días (referencia: mismo criterio que baja los días efectivos)
          const montoProporcionalDiasLicGoce = aplicaDescuentoDiasLicGoce && diasLicenciaGoceEnPeriodo > 0
            ? montoPorDia * diasLicenciaGoceEnPeriodo
            : 0;
          console.log('🧾 [LIC. GOCE HABER] Este ingreso →', {
            codigo_plame: ingreso.concepto.plame_configuracion.codigo_plame,
            plame_id: ingreso.concepto.plame_configuracion.id,
            en_config: aplicaDescuentoDiasLicGoce
              ? 'SÍ: se restan días tareo lic. con goce al proporcional'
              : 'NO: no se descuenta nada; el concepto no está en la configuración',
            dias_tareo_licencia_con_goce: diasLicenciaGoceEnPeriodo,
            dias_descontados_al_proporcional: diasLicenciaGoceAplicado,
            monto_concepto: montoConcepto,
            dias_base_mes: diasBaseMes,
            monto_por_dia: Number(montoPorDia.toFixed(4)),
            monto_por_dia_por_dias_lic_goce: Number(montoProporcionalDiasLicGoce.toFixed(2))
          });
          
          // 🧮 CÁLCULO DETALLADO DE DÍAS EFECTIVOS
          console.log('\n📊 === CÁLCULO DE DÍAS EFECTIVOS ===');
          console.log(`📅 Días calculados (considerando ingreso/cese): ${diasMaximosPeriodoCalculados}`);
          console.log(`📅 Días máximos del período (limitado a 30): ${diasMaximosPeriodo}`);
          console.log(`📋 Días especiales a descontar (vacaciones): ${diasEspecialesDescuentoAplicado}${exclVac ? ' (concepto no afecto a vacaciones)' : ''}`);
          console.log(
            `📋 Días licencia con goce a restar: ${diasLicenciaGoceAplicado} (solo si el concepto está en config.; tareo: licencia_con_goce_de_haber)`
          );
          
          const diasDespuesDescuento = diasMaximosPeriodo - diasEspecialesDescuentoAplicado - diasLicenciaGoceAplicado;
          console.log(`➖ Días después de descontar vacaciones y lic. goce: ${diasMaximosPeriodo} - ${diasEspecialesDescuentoAplicado} - ${diasLicenciaGoceAplicado} = ${diasDespuesDescuento}`);
          const esFebrero = datosDisponibles.analisisPeriodo.mes?.numero === 2;
          let diasDespuesDescuentoFinal = diasDespuesDescuento;
          if (esFebrero && diasEspecialesDescuentoAplicado > 0) {
            diasDespuesDescuentoFinal = Math.min(30, diasDespuesDescuento + 2);
            console.log(`📅 Febrero con vacaciones: se suman 2 días → ${diasDespuesDescuentoFinal} días`);
          }
          
          const diasMinimos = Math.min(diasMaximosPeriodo, diasDespuesDescuentoFinal);
          console.log(`🔽 Mínimo entre días máximos y días después de descuento: Math.min(${diasMaximosPeriodo}, ${diasDespuesDescuentoFinal}) = ${diasMinimos}`);
          
          let diasEfectivos = Math.min(30, diasMinimos);
          if (esFebrero && (diasEfectivos === 28 || diasEfectivos === 29)) {
            diasEfectivos = 30;
            console.log(`📅 Período febrero: 28/29 días se ajustan a 30 para efectos del cálculo`);
          }
          console.log(`🔽 Días efectivos finales (límite 30): ${diasEfectivos}`);
          console.log(`✅ RESULTADO FINAL: ${diasEfectivos} días efectivos para sueldo básico\n`);
          console.log('Días del mes (base proporcional):', datosDisponibles.analisisPeriodo.mes.diasCompletosDelMes);
          let montoDiasTrabajados = parseFloat(((montoConcepto / diasBaseMes) * diasEfectivos).toFixed(2));
          console.log('🔍 Monto de días trabajados:', montoDiasTrabajados);
          if (aplicaDescuentoDiasLicGoce && diasLicenciaGoceAplicado > 0) {
            const mDesc = Number(montoProporcionalDiasLicGoce.toFixed(2));
            console.log(
              `💸 Monto descontado por licencia con goce de haber: S/ ${mDesc} (${diasLicenciaGoceAplicado} d. × S/ ${Number(montoPorDia.toFixed(4))} monto/día; base: monto S/ ${Number(montoConcepto.toFixed(2))} ÷ ${diasBaseMes} d. del mes)`
            );
          } else {
            console.log('💸 Monto descontado por licencia con goce de haber: S/ 0.00 (no aplica: concepto no está en la config. o 0 días tareo lic. con goce)');
          }

          //console.log('Tendra asignacion familiar?');
          //console.log('Codigo de ingreso:', ingreso.concepto);
          
          // 🔧 Concepto estático: usar monto de ficha (no prorratear por días/vacaciones).
          const esConceptoEstatico = conceptoEstaEnEstaticos(
            datosDisponibles.conceptosEstaticos.completos,
            ingreso.concepto
          );
          const conservarMontoEstatico = debeConservarMontoEstaticoFicha(
            esConceptoEstatico,
            esRegimenAgrario,
            codigoPlameIngreso
          );
          const tieneMesParcialPorHistorial = diasMaximosPeriodoCalculados < diasBaseMes;
          const usarMontoEstaticoCompleto0309 = debeUsarMontoEstaticoCompleto0309(
            codigoPlameIngreso,
            esConceptoEstatico,
            tasaNocturnaPrioriza0309
          );
          if (conservarMontoEstatico) {
            montoDiasTrabajados = parseFloat(ingreso.concepto.monto) || 0;
            console.log(
              `🔧 Concepto ${codigoPlameIngreso} estático: se conserva monto de ficha S/ ${montoDiasTrabajados} (sin prorrateo)`
            );
          } else if (
            (exclVac) &&
            (!tieneMesParcialPorHistorial || usarMontoEstaticoCompleto0309)
          ) {
             montoDiasTrabajados = parseFloat(ingreso.concepto.monto);
           } else if ((esConceptoEstatico || exclVac) && tieneMesParcialPorHistorial) {
             console.log(
               `📅 Concepto ${ingreso.concepto.plame_configuracion.codigo_plame}: mes parcial por historial (${diasMaximosPeriodoCalculados}/${diasBaseMes} d.) → proporcional S/ ${montoDiasTrabajados}`
             );
           }

          if (String(codigoPlameIngreso) === CODIGO_PLAME_BONIFICACION_NOCTURNA) {
            logDiagnosticoBonificacionNocturna('0309 bucle proporcional/estático (mes único)', {
              monto_plantilla: montoConcepto,
              monto_calculado: montoDiasTrabajados,
              es_concepto_estatico: Boolean(esConceptoEstatico),
              mes_parcial_historial: tieneMesParcialPorHistorial,
              usar_monto_estatico_completo_0309: usarMontoEstaticoCompleto0309,
              dias_efectivos: diasEfectivos,
              dias_base_mes: diasBaseMes,
              tasa_nocturna_prioriza: tasaNocturnaPrioriza0309,
              regla_aplicada:
                tasaNocturnaPrioriza0309
                  ? 'omitido_en_bucle'
                  : usarMontoEstaticoCompleto0309
                    ? 'estatico_monto_plantilla_completo (0309 en config estáticos, tasa off)'
                    : esConceptoEstatico && !tieneMesParcialPorHistorial
                      ? 'estatico_monto_plantilla'
                      : esConceptoEstatico && tieneMesParcialPorHistorial
                        ? 'estatico_proporcional_mes_parcial'
                        : 'proporcional_normal',
            });
          }
          
        Ingresos.push({
            codigoPlame: ingreso.concepto.plame_configuracion.codigo_plame,
            concepto: ingreso.concepto.plame_configuracion.descripcion,
            id: ingreso.concepto.id,
            monto: montoDiasTrabajados,
            plame_configuracion_id: ingreso.concepto.plame_configuracion.id,
            tipo_concepto: ingreso.concepto.tipo_concepto,
            en_config_licencia_goce_haber: aplicaDescuentoDiasLicGoce,
            dias_licencia_goce_haber: diasLicenciaGoceAplicado,
            monto_descuento_licencia_goce_haber:
              aplicaDescuentoDiasLicGoce && diasLicenciaGoceAplicado > 0
                ? Number(montoProporcionalDiasLicGoce.toFixed(2))
                : 0
        });
            
        console.log('Ingresos:', Ingresos);
                  } else if (datosDisponibles.analisisPeriodo.tipo === 'dos_meses') { 
            //  console.log('🔍 Dos meses');
              //console.log('🔍 Días trabajados:', datosDisponibles.analisisPeriodo.detallesDias);
              
              // Calcular días efectivos considerando cese del empleado
              const diasMaximosPeriodoDosCalculados = calcularDiasEfectivos(datosDisponibles.analisisPeriodo, datosDisponibles.historialLaboral);
              // ✅ LIMITAR A MÁXIMO 30 DÍAS (ya no 30 o 31, siempre 30)
              const diasMaximosPeriodoDos = Math.min(30, diasMaximosPeriodoDosCalculados);
              
              const exclVacDos = ingresoNoAfectaVacaciones(ingreso.concepto.plame_configuracion.id);

              // Contar días especiales para restar del cálculo del sueldo básico
              const diasEspecialesDescuentoDos = datosTareosDetalle.filter(tareo => 
                ['vacaciones'].includes(tareo.tipo_evento)
              ).length;
              const diasEspecialesDescuentoDosAplicado = exclVacDos ? 0 : diasEspecialesDescuentoDos;
              const aplicaDescuentoDiasLicGoceDos = ingresoDescuentaDiasLicenciaGoce(ingreso.concepto.plame_configuracion.id);
              const diasLicenciaGoceAplicadoDos = aplicaDescuentoDiasLicGoceDos ? diasLicenciaGoceEnPeriodo : 0;
              const diasReferenciaCalculo = datosDisponibles.analisisPeriodo.primerMes.diasCompletosDelMes;
              const montoConceptoDos = parseFloat(ingreso.concepto.monto) || 0;
              const montoPorDiaDos = diasReferenciaCalculo > 0 ? montoConceptoDos / diasReferenciaCalculo : 0;
              const montoProporcionalDiasLicGoceDos = aplicaDescuentoDiasLicGoceDos && diasLicenciaGoceEnPeriodo > 0
                ? montoPorDiaDos * diasLicenciaGoceEnPeriodo
                : 0;
              console.log('🧾 [LIC. GOCE HABER] Este ingreso (dos meses) →', {
                codigo_plame: ingreso.concepto.plame_configuracion.codigo_plame,
                plame_id: ingreso.concepto.plame_configuracion.id,
                en_config: aplicaDescuentoDiasLicGoceDos
                  ? 'SÍ: se restan días tareo lic. con goce al proporcional'
                  : 'NO: no se descuenta nada; el concepto no está en la configuración',
                dias_tareo_licencia_con_goce: diasLicenciaGoceEnPeriodo,
                dias_descontados_al_proporcional: diasLicenciaGoceAplicadoDos,
                monto_concepto: montoConceptoDos,
                dias_base_mes: diasReferenciaCalculo,
                monto_por_dia: Number(montoPorDiaDos.toFixed(4)),
                monto_por_dia_por_dias_lic_goce: Number(montoProporcionalDiasLicGoceDos.toFixed(2))
              });
              
              // 🧮 CÁLCULO DETALLADO DE DÍAS EFECTIVOS (DOS MESES)
              console.log('\n📊 === CÁLCULO DE DÍAS EFECTIVOS (DOS MESES) ===');
              console.log(`📅 Días calculados (considerando ingreso/cese): ${diasMaximosPeriodoDosCalculados}`);
              console.log(`📅 Días máximos del período (limitado a 30): ${diasMaximosPeriodoDos}`);
              console.log(`📋 Días especiales a descontar (vacaciones): ${diasEspecialesDescuentoDosAplicado}${exclVacDos ? ' (concepto no afecto a vacaciones)' : ''}`);
              console.log(
                `📋 Días licencia con goce a restar: ${diasLicenciaGoceAplicadoDos} (solo si el concepto está en config.; tareo: licencia_con_goce_de_haber)`
              );
              
              const diasDespuesDescuentoDos = diasMaximosPeriodoDos - diasEspecialesDescuentoDosAplicado - diasLicenciaGoceAplicadoDos;
              console.log(`➖ Días después de descontar vacaciones y lic. goce: ${diasMaximosPeriodoDos} - ${diasEspecialesDescuentoDosAplicado} - ${diasLicenciaGoceAplicadoDos} = ${diasDespuesDescuentoDos}`);
              const esFebreroDos = datosDisponibles.analisisPeriodo.primerMes?.numero === 2 || datosDisponibles.analisisPeriodo.segundoMes?.numero === 2;
              let diasDespuesDescuentoDosFinal = diasDespuesDescuentoDos;
              if (esFebreroDos && diasEspecialesDescuentoDosAplicado > 0) {
                diasDespuesDescuentoDosFinal = Math.min(30, diasDespuesDescuentoDos + 2);
                console.log(`📅 Febrero con vacaciones (dos meses): se suman 2 días → ${diasDespuesDescuentoDosFinal} días`);
              }
              
              const diasMinimosDos = Math.min(diasMaximosPeriodoDos, diasDespuesDescuentoDosFinal);
              console.log(`🔽 Mínimo entre días máximos y días después de descuento: Math.min(${diasMaximosPeriodoDos}, ${diasDespuesDescuentoDosFinal}) = ${diasMinimosDos}`);
              
              let diasEfectivosDos = Math.min(30, diasMinimosDos);
              if (esFebreroDos && (diasEfectivosDos === 28 || diasEfectivosDos === 29)) {
                diasEfectivosDos = 30;
                console.log(`📅 Período incluye febrero: 28/29 días se ajustan a 30 para efectos del cálculo`);
              }
              console.log(`🔽 Días efectivos finales (límite 30): ${diasEfectivosDos}`);
              console.log(`✅ RESULTADO FINAL: ${diasEfectivosDos} días efectivos para sueldo básico (dos meses)\n`);
              
              // Para dos meses, usar proporción del primer mes por simplicidad (diasReferenciaCalculo ya arriba)
              let montoDiasTrabajados = parseFloat(((montoConceptoDos / diasReferenciaCalculo) * diasEfectivosDos).toFixed(2));
              console.log('🔍 Monto de días trabajados (dos meses):', montoDiasTrabajados);
              if (aplicaDescuentoDiasLicGoceDos && diasLicenciaGoceAplicadoDos > 0) {
                const mDescDos = Number(montoProporcionalDiasLicGoceDos.toFixed(2));
                console.log(
                  `💸 Monto descontado por licencia con goce de haber: S/ ${mDescDos} (${diasLicenciaGoceAplicadoDos} d. × S/ ${Number(montoPorDiaDos.toFixed(4))} monto/día; base: monto S/ ${Number(montoConceptoDos.toFixed(2))} ÷ ${diasReferenciaCalculo} d. base (1.er mes))`
                );
              } else {
                console.log('💸 Monto descontado por licencia con goce de haber: S/ 0.00 (no aplica: concepto no está en la config. o 0 días tareo lic. con goce)');
              }
              
              // 🔧 Concepto estático (dos meses): monto de ficha, sin prorrateo.
              const esConceptoEstaticoDos = conceptoEstaEnEstaticos(
                datosDisponibles.conceptosEstaticos.completos,
                ingreso.concepto
              );
              const codigoPlameDos = ingreso.concepto.plame_configuracion.codigo_plame;
              const conservarMontoEstaticoDos = debeConservarMontoEstaticoFicha(
                esConceptoEstaticoDos,
                esRegimenAgrario,
                codigoPlameDos
              );
              const tieneMesParcialPorHistorialDos = diasMaximosPeriodoDosCalculados < diasReferenciaCalculo;
              const usarMontoEstaticoCompleto0309Dos = debeUsarMontoEstaticoCompleto0309(
                codigoPlameDos,
                esConceptoEstaticoDos,
                tasaNocturnaPrioriza0309
              );
            if (conservarMontoEstaticoDos) {
                  montoDiasTrabajados = parseFloat(ingreso.concepto.monto) || 0;
                  console.log(
                    `🔧 Concepto ${codigoPlameDos} estático (dos meses): se conserva monto de ficha S/ ${montoDiasTrabajados} (sin prorrateo)`
                  );
              } else if (
                (exclVacDos) &&
                (!tieneMesParcialPorHistorialDos || usarMontoEstaticoCompleto0309Dos)
              ) {
                  montoDiasTrabajados = parseFloat(ingreso.concepto.monto);
              } else if ((esConceptoEstaticoDos || exclVacDos) && tieneMesParcialPorHistorialDos) {
                  console.log(
                    `📅 Concepto ${codigoPlameDos} (dos meses): mes parcial por historial (${diasMaximosPeriodoDosCalculados}/${diasReferenciaCalculo} d.) → proporcional S/ ${montoDiasTrabajados}`
                  );
              }
            Ingresos.push({
                codigoPlame: ingreso.concepto.plame_configuracion.codigo_plame,
                concepto: ingreso.concepto.plame_configuracion.descripcion,
                id: ingreso.concepto.id,
                monto: montoDiasTrabajados,
                plame_configuracion_id: ingreso.concepto.plame_configuracion.id,
                tipo_concepto: ingreso.concepto.tipo_concepto,
                en_config_licencia_goce_haber: aplicaDescuentoDiasLicGoceDos,
                dias_licencia_goce_haber: diasLicenciaGoceAplicadoDos,
                monto_descuento_licencia_goce_haber:
                  aplicaDescuentoDiasLicGoceDos && diasLicenciaGoceAplicadoDos > 0
                    ? Number(montoProporcionalDiasLicGoceDos.toFixed(2))
                    : 0
            });
        }
        
    })
    
    // ahora vamos a sacar los conceptos de los tareos (SOLO DEL PERÍODO DE LA PLANILLA)

    const datosTareos = datosDisponibles.tareos.tareosDetallados;
    const conceptosDeTareos = [];

    const tareosParaIngresos = datosTareos.forEach(tareo => {
        if (tareo.conceptos.length > 0) {
                         const conceptodetallado = tareo.conceptos.forEach(concepto => {
                 if (concepto.tipo_concepto === 'ingreso') {
                     const tb = String(concepto.tipo_boleta || '').toLowerCase().trim();
                     const plameId = concepto.plame_configuracion_id || 
                                   concepto.plame_configuracion?.id || 
                                   concepto.id_plame_configuracion ||
                                   concepto.plame_configuracion;
                     const codigoVia = concepto.plame_configuracion?.codigo_plame
                       || concepto.codigo_plame
                       || concepto.codigo
                       || '';
                     const esVia = esViaticoDeclaracion(plameId, codigoVia);

                     // Viáticos: solo a PLAME si destino explícito es plame (no interna / vacío)
                     if (esVia) {
                       if (tb !== 'plame') return;
                     } else if (tb === 'interna') {
                       // Conceptos solo-interna no van a la boleta PLAME
                       return;
                     }

                     conceptosDeTareos.push({
                         id_plame_configuracion: plameId,
                         monto: parseFloat(concepto.monto) || 0,
                     });
                 }   
             })
        }
    })
    //console.log('🔍 Conceptos de tareos antes de consolidar:', conceptosDeTareos);
    
    // Consolidar conceptos duplicados sumando sus montos
    const conceptosConsolidados = conceptosDeTareos.reduce((acc, concepto) => {
        const existente = acc.find(item => item.id_plame_configuracion === concepto.id_plame_configuracion);
        
        if (existente) {
            // Si ya existe, sumar el monto
            existente.monto += concepto.monto;
        } else {
            // Si no existe, agregar nuevo
            acc.push({ ...concepto });
        }
        
        return acc;
    }, []);
    
    //console.log('🔍 Conceptos de tareos consolidados:', conceptosConsolidados);
    
    // Procesar conceptos consolidados para agregar a Ingresos
    conceptosConsolidados.forEach(conceptoConsolidado => {
        if (conceptoConsolidado.id_plame_configuracion) {
            // Buscar la información completa en conceptosAfectosTareos
            const conceptoAfecto = Object.values(datosDisponibles.apis.conceptosPlame).find(correlacion => 
                correlacion.id === conceptoConsolidado.id_plame_configuracion
            );
            
            if (conceptoAfecto) {
      //          console.log('🔍 Concepto afecto encontrado:', conceptoAfecto);
                if (debeOmitir0309EnProcesamientoPlantilla(conceptoAfecto.codigo_plame, tasaNocturna)) {
                  console.log(
                    `🌙 ${CODIGO_PLAME_BONIFICACION_NOCTURNA} de tareos omitido: tasa nocturna activa en planilla`
                  );
                  return;
                }
                if (
                  debeOmitir0309DeTareos(
                    conceptoAfecto.codigo_plame,
                    es0309EstaticoEmpresa,
                    tasaNocturna
                  )
                ) {
                  console.log(
                    `🌙 ${CODIGO_PLAME_BONIFICACION_NOCTURNA} de tareos omitido: concepto estático en plantilla (tasa off)`
                  );
                  return;
                }
                  Ingresos.push({
                     codigoPlame: conceptoAfecto.codigo_plame,
                     concepto: conceptoAfecto.descripcion,
                     id: conceptoAfecto.id,
                     monto: conceptoConsolidado.monto,
                     plame_configuracion_id: conceptoAfecto.id,
                     tipo_concepto: 'ingreso',
                     ...(esViaticoDeclaracion(conceptoAfecto.id, conceptoAfecto.codigo_plame)
                       ? {
                           origenCalculo: 'viatico',
                           excluirBoletaOrdinaria: true,
                         }
                       : {}),
                     ...ingresoSinDescuentoLicGoceHaber()
                 });
            } else {
        //        console.log('⚠️ No se encontró concepto afecto para ID:', conceptoConsolidado.id_plame_configuracion);
            }
        }
    });

    
    // listo ahora necesitamos sacar las horas extras
    const horasExtras = datosDisponibles.horasExtras;
    //console.log('🔍 Horas extras disponibles:', horasExtras);
    
    // Obtener sueldo básico desde datosDisponibles (código PLAME 0121)
    const conceptoSueldoBasico = datosDisponibles.conceptosIngresos.find(ingreso => 
        ingreso.plame_configuracion.codigo_plame === '0121'
    );
    const sueldoBasico = conceptoSueldoBasico ? parseFloat(conceptoSueldoBasico.monto) : 0;
    //console.log('🔍 Concepto sueldo básico encontrado:', conceptoSueldoBasico);
    //console.log('🔍 Sueldo básico:', sueldoBasico);
    
    if (sueldoBasico && sueldoBasico > 0) {
        // Calcular valor hora: sueldo_basico / 30 días / 8 horas
        const valorHora = sueldoBasico / 30 / 8;
      //  console.log('🔍 Valor por hora:', valorHora);
        
        // Procesar horas extras al 25%
        if (horasExtras.horasExtra25 > 0) {
            const montoHorasExtra25 = parseFloat((valorHora * 1.25 * horasExtras.horasExtra25).toFixed(2));
        //    console.log('🔍 Monto horas extra 25%:', montoHorasExtra25);
            
            Ingresos.push({
                codigoPlame: '0105',
                concepto: 'TRABAJO EN SOBRETIEMPO (HORAS EXTRAS) 25%',
                id: 33,
                monto: montoHorasExtra25,
                plame_configuracion_id: 33,
                tipo_concepto: 'ingreso',
                ...ingresoSinDescuentoLicGoceHaber()
            });
        }
        
        // Procesar horas extras al 35%
        if (horasExtras.horasExtra35 > 0) {
            const montoHorasExtra35 = parseFloat((valorHora * 1.35 * horasExtras.horasExtra35).toFixed(2));
          //  console.log('🔍 Monto horas extra 35%:', montoHorasExtra35);
            
            Ingresos.push({
                codigoPlame: '0106',
                concepto: 'TRABAJO EN SOBRETIEMPO (HORAS EXTRAS) 35%',
                id: 34,
                monto: montoHorasExtra35,
                plame_configuracion_id: 34,
                tipo_concepto: 'ingreso',
                ...ingresoSinDescuentoLicGoceHaber()
            });
        }
        
        //console.log('🔍 Total horas extras procesadas - 25%:', horasExtras.horasExtra25, 'hrs | 35%:', horasExtras.horasExtra35, 'hrs');
    } else {
        //console.log('⚠️ No se puede calcular horas extras: sueldo básico no válido');
    }

    // 0309: si tasa nocturna está activa en planilla → cálculo por tareos; si no → reglas de concepto estático (bucle anterior)
    const filas0309AntesTasa = Ingresos.filter(
      (i) => String(i.codigoPlame) === CODIGO_PLAME_BONIFICACION_NOCTURNA
    );
    logDiagnosticoBonificacionNocturna('Antes bloque tasa nocturna', {
      tasaNocturnaPrioriza0309,
      tasaNocturna_activo: tasaNocturna?.activo,
      tasaNocturna_montoTotal: tasaNocturna?.montoTotal,
      filas_0309_en_ingresos: filas0309AntesTasa,
    });

    if (tasaNocturnaPrioriza0309 && tasaNocturna?.montoTotal > 0) {
        const conceptoNocturno = tasaNocturna.conceptoPlame;
        const plameIdNocturno = conceptoNocturno?.id;
        const codigoNocturno = conceptoNocturno?.codigo_plame || CODIGO_PLAME_BONIFICACION_NOCTURNA;
        for (let i = Ingresos.length - 1; i >= 0; i -= 1) {
          if (String(Ingresos[i].codigoPlame) === codigoNocturno) {
            Ingresos.splice(i, 1);
          }
        }
        Ingresos.push({
            codigoPlame: codigoNocturno,
            concepto: conceptoNocturno?.descripcion || 'BONIFICACIÓN POR TURNO NOCTURNO',
            id: plameIdNocturno ?? 0,
            monto: tasaNocturna.montoTotal,
            plame_configuracion_id: plameIdNocturno ?? 0,
            tipo_concepto: 'ingreso',
            ...ingresoSinDescuentoLicGoceHaber()
        });
        logDiagnosticoBonificacionNocturna('0309 inyectado por TASA NOCTURNA (tareos)', {
          monto: tasaNocturna.montoTotal,
          dias: tasaNocturna.dias,
          horasPorDia: tasaNocturna.horasPorDia,
          horasTotales: tasaNocturna.horasTotales,
          remuneracion_basica_usada: tasaNocturna.remuneracionBasica,
        });
    } else if (!tasaNocturnaPrioriza0309) {
        logDiagnosticoBonificacionNocturna('Tasa nocturna NO prioriza → 0309 vía estático/proporcional', {
          motivo_no_tasa:
            !tasaNocturna?.activo
              ? 'tasaNocturna.activo=false (config desactivada o sin días/sueldo/horas)'
              : tasaNocturna?.calcularEnPlanilla === false
                ? 'calcular_en_planilla=false'
                : (tasaNocturna?.montoTotal ?? 0) <= 0
                  ? 'montoTotal=0'
                  : 'desconocido',
          filas_0309_actuales: Ingresos.filter((i) => String(i.codigoPlame) === CODIGO_PLAME_BONIFICACION_NOCTURNA),
        });
    } else {
        logDiagnosticoBonificacionNocturna('Tasa prioriza pero montoTotal=0 → sin inyectar 0309', {
          tasaNocturna,
          filas_0309_actuales: Ingresos.filter((i) => String(i.codigoPlame) === CODIGO_PLAME_BONIFICACION_NOCTURNA),
        });
    }

    logDiagnosticoBonificacionNocturna('Resumen final 0309 en Ingresos', {
      filas: Ingresos.filter((i) => String(i.codigoPlame) === CODIGO_PLAME_BONIFICACION_NOCTURNA),
    });

    const fila0309Final = Ingresos.find((i) => String(i.codigoPlame) === CODIGO_PLAME_BONIFICACION_NOCTURNA);
    if (fila0309Final) {
      const montoFinal = parseFloat(fila0309Final.monto) || 0;
      const montoPlantilla = parseFloat(concepto0309Plantilla?.monto ?? 0);
      const origen0309 =
        tasaNocturnaPrioriza0309 && Math.abs(montoFinal - (tasaNocturna?.montoTotal ?? 0)) < 0.02
          ? 'TASA_NOCTURNA_TAREOS'
          : montoPlantilla > 0 && Math.abs(montoFinal - montoPlantilla) < 0.02
            ? 'CONCEPTO_ESTATICO_MONTO_COMPLETO'
            : montoPlantilla > 0
              ? 'CONCEPTO_ESTATICO_PROPORCIONAL (monto_plantilla/días_mes × días_efectivos)'
              : 'OTRO';
      logDiagnosticoBonificacionNocturnaVisible('ORIGEN MONTO 0309', {
        empleado_id: empleado.empleado_id,
        empleado_nombre: empleado.empleado_nombre,
        monto_final: montoFinal,
        origen: origen0309,
        config_api_activo: configTasaNocturnaApi?.activo ?? null,
        tasaNocturna_armada_activo: tasaNocturna?.activo ?? null,
        tasaNocturna_prioriza: tasaNocturnaPrioriza0309,
        tasaNocturna_montoTotal: tasaNocturna?.montoTotal ?? null,
        monto_plantilla_0309: montoPlantilla || null,
        nota:
          origen0309 === 'CONCEPTO_ESTATICO_PROPORCIONAL (monto_plantilla/días_mes × días_efectivos)'
            ? `Ej: ${montoPlantilla}/30×días ≈ ${montoFinal} — NO es tasa nocturna aunque config esté desactivada`
            : undefined,
      });
    } else {
      logDiagnosticoBonificacionNocturnaVisible('Sin fila 0309 en Ingresos', {
        empleado_id: empleado.empleado_id,
        empleado_nombre: empleado.empleado_nombre,
        config_api_activo: configTasaNocturnaApi?.activo ?? null,
        tasaNocturna_prioriza: tasaNocturnaPrioriza0309,
      });
    }

    // ahora los conceptos de vacaciones, feriados laborados y descansos laborados
    const datosTareosEspeciales = datosDisponibles.tareos.tareosDetallados;
    
    const listaFeriadosConfig = datosDisponibles.apis.feriadosPagoDoble || [];
    const fechasFeriadoPagoDoble = new Set(
        listaFeriadosConfig
            .map((f) => normalizarFechaYYYYMMDD(f.fecha))
            .filter(Boolean)
    );

    console.log('🎉 Feriados pago doble (datosAPIs):', listaFeriadosConfig.length, 'registro(s)');
    console.log('🎉 Fechas configuradas (YYYY-MM-DD):', [...fechasFeriadoPagoDoble]);

    const esFeriadoLaborado = (tipo) => {
        const t = String(tipo || '').toLowerCase().trim().replace(/\s+/g, '_');
        return t === 'feriado_laborado';
    };

    // Contar días por tipo de evento especial
    const diasEspeciales = {
        vacaciones: 0,
        feriado_laborado: 0,
        feriado_laborado_pago_doble: 0,
        descanso_laborado: 0,
        descanso_medico: 0,
        reposo_medico: 0,
        subsidio_por_enfermedad: 0,
        licencia_con_goce_de_haber: 0
    };
    
    // Contar días de eventos especiales
    datosTareosEspeciales.forEach(tareo => {
        if (tareo.tipo_evento === 'vacaciones') {
            diasEspeciales.vacaciones++;
        } else if (esFeriadoLaborado(tareo.tipo_evento)) {
            const fechaTareo = normalizarFechaYYYYMMDD(tareo.fecha);
            const aplicaPagoDoble = fechaTareo && fechasFeriadoPagoDoble.has(fechaTareo);
            console.log(
                `🎉 Feriado laborado: tareo=${tareo.fecha} → ${fechaTareo} | ` +
                `¿coincide con config? ${aplicaPagoDoble ? 'SÍ (2 jornales)' : 'NO (1 jornal)'}`
            );
            if (aplicaPagoDoble) {
                diasEspeciales.feriado_laborado_pago_doble++;
            } else {
                diasEspeciales.feriado_laborado++;
            }
        } else if (tareo.tipo_evento === 'descanso_laborado') {
            diasEspeciales.descanso_laborado++;
        } else if (tareo.tipo_evento === 'descanso_medico') {
            diasEspeciales.descanso_medico++;
        } else if (tareo.tipo_evento === 'reposo_medico') {
            diasEspeciales.reposo_medico++;
        } else if (tareo.tipo_evento === 'subsidio_por_enfermedad') {
            diasEspeciales.subsidio_por_enfermedad++;
        } else if (tareo.tipo_evento === 'licencia_con_goce_de_haber') {
            diasEspeciales.licencia_con_goce_de_haber++;
        }
    });
    
    console.log('🔍 Días especiales contados:', diasEspeciales);
    
    // Obtener sueldo básico para los cálculos desde datosDisponibles
    const conceptoSueldoBasicoEspecial = datosDisponibles.conceptosIngresos.find(ingreso => 
        ingreso.plame_configuracion.codigo_plame === '0121'
    );
    const sueldoBasicoEspecial = conceptoSueldoBasicoEspecial ? parseFloat(conceptoSueldoBasicoEspecial.monto) : 0;
    
    if (sueldoBasicoEspecial > 0) {
        console.log('🔍 Sueldo básico para cálculos especiales:', sueldoBasicoEspecial);
        
        // VACACIONES - Código PLAME 0118
        if (diasEspeciales.vacaciones > 0) {
            let montoVacaciones = 0;
            
            if (datosDisponibles.analisisPeriodo.tipo === 'mes_unico') {
                // Un solo mes
                const diasDelMes = datosDisponibles.analisisPeriodo.mes.diasCompletosDelMes;
                montoVacaciones = parseFloat(((sueldoBasicoEspecial / diasDelMes) * diasEspeciales.vacaciones).toFixed(2));
                console.log(`🔍 Vacaciones mes único: ${sueldoBasicoEspecial}/${diasDelMes}*${diasEspeciales.vacaciones} = ${montoVacaciones}`);
            } else if (datosDisponibles.analisisPeriodo.tipo === 'dos_meses') {
                // Necesito distribuir proporcionalmente entre los dos meses
                // Por simplicidad, uso el mes con más días de la planilla
                const diasPrimerMes = datosDisponibles.analisisPeriodo.primerMes.diasCompletosDelMes;
                const diasSegundoMes = datosDisponibles.analisisPeriodo.segundoMes.diasCompletosDelMes;
                const diasMesPrincipal = Math.max(diasPrimerMes, diasSegundoMes);
                montoVacaciones = parseFloat(((sueldoBasicoEspecial / diasMesPrincipal) * diasEspeciales.vacaciones).toFixed(2));
                console.log(`🔍 Vacaciones dos meses: ${sueldoBasicoEspecial}/${diasMesPrincipal}*${diasEspeciales.vacaciones} = ${montoVacaciones}`);
            }
            
            Ingresos.push({
                codigoPlame: '0118',
                concepto: 'REMUNERACIÓN VACACIONAL',
                id: 46,
                monto: montoVacaciones,
                plame_configuracion_id: 46,
                tipo_concepto: 'ingreso',
                ...ingresoSinDescuentoLicGoceHaber()
            });
        }
        
        // FERIADOS LABORADOS Y DESCANSOS LABORADOS - Código PLAME 0107
        // Feriado normal = 1 jornal; feriado en fecha configurada (pago doble) = 2 jornales; descanso laborado = 1
        const totalDiasEquivalentesFeriadosDescansos =
            diasEspeciales.feriado_laborado
            + (diasEspeciales.feriado_laborado_pago_doble * 2)
            + diasEspeciales.descanso_laborado;
        if (totalDiasEquivalentesFeriadosDescansos > 0) {
            let montoFeriadosDescansos = 0;
            
            if (datosDisponibles.analisisPeriodo.tipo === 'mes_unico') {
                // Un solo mes
                const diasDelMes = datosDisponibles.analisisPeriodo.mes.diasCompletosDelMes;
                montoFeriadosDescansos = parseFloat(((sueldoBasicoEspecial / diasDelMes) * totalDiasEquivalentesFeriadosDescansos).toFixed(2));
                console.log(`🔍 Feriados/Descansos mes único: ${sueldoBasicoEspecial}/${diasDelMes}*${totalDiasEquivalentesFeriadosDescansos} (normal:${diasEspeciales.feriado_laborado}, doble:${diasEspeciales.feriado_laborado_pago_doble}, descanso:${diasEspeciales.descanso_laborado}) = ${montoFeriadosDescansos}`);
            } else if (datosDisponibles.analisisPeriodo.tipo === 'dos_meses') {
                // Necesito distribuir proporcionalmente entre los dos meses
                const diasPrimerMes = datosDisponibles.analisisPeriodo.primerMes.diasCompletosDelMes;
                const diasSegundoMes = datosDisponibles.analisisPeriodo.segundoMes.diasCompletosDelMes;
                const diasMesPrincipal = Math.max(diasPrimerMes, diasSegundoMes);
                montoFeriadosDescansos = parseFloat(((sueldoBasicoEspecial / diasMesPrincipal) * totalDiasEquivalentesFeriadosDescansos).toFixed(2));
                console.log(`🔍 Feriados/Descansos dos meses: ${sueldoBasicoEspecial}/${diasMesPrincipal}*${totalDiasEquivalentesFeriadosDescansos} (normal:${diasEspeciales.feriado_laborado}, doble:${diasEspeciales.feriado_laborado_pago_doble}, descanso:${diasEspeciales.descanso_laborado}) = ${montoFeriadosDescansos}`);
            }
            
            Ingresos.push({
                codigoPlame: '0107',
                concepto: 'TRABAJO EN DÍA FERIADO O DÍA DE DESCANSO',
                id: 35,
                monto: montoFeriadosDescansos,
                plame_configuracion_id: 35,
                tipo_concepto: 'ingreso',
                ...ingresoSinDescuentoLicGoceHaber()
            });
        }
        
        // ❌ DESCANSOS MÉDICOS REMOVIDOS - Ya no se calculan licencias con goce de haber
        // Los descansos médicos y reposos médicos ya no generan ingresos automáticamente
        console.log('ℹ️ Descansos médicos y reposos médicos detectados pero no se procesan como licencias con goce');

        // Conceptos PLAME distintos: 0916 = subsidio por incapacidad/enfermedad; 0907 = licencia con goce de haber
        const listaPlame = (() => {
            const p = datosDisponibles.apis.conceptosPlame;
            return Array.isArray(p) ? p : Object.values(p || {});
        })();
        const plameIngreso0916 = listaPlame.find(c => String(c.codigo_plame) === '0916');
        if (!plameIngreso0916 && diasEspeciales.subsidio_por_enfermedad > 0) {
            console.warn('⚠️ No hay concepto PLAME 0916 (subsidio por enfermedad) en conceptosPlame. Verificar catálogo.');
        }

        // SUBSIDIO POR ENFERMEDAD / INCAPACIDAD — Código PLAME 0916 (solo tareo subsidio_por_enfermedad)
        const diasSoloSubsidioEnf = diasEspeciales.subsidio_por_enfermedad;
        if (diasSoloSubsidioEnf > 0) {
            let montoSubsidioEnfermedad = 0;
            if (datosDisponibles.analisisPeriodo.tipo === 'mes_unico') {
                const diasDelMes = datosDisponibles.analisisPeriodo.mes.diasCompletosDelMes;
                montoSubsidioEnfermedad = parseFloat(((sueldoBasicoEspecial / diasDelMes) * diasSoloSubsidioEnf).toFixed(2));
                console.log(`🔍 Subsidio (0916) mes único: ${sueldoBasicoEspecial}/${diasDelMes}*${diasSoloSubsidioEnf} = ${montoSubsidioEnfermedad}`);
            } else if (datosDisponibles.analisisPeriodo.tipo === 'dos_meses') {
                const diasPrimerMes = datosDisponibles.analisisPeriodo.primerMes.diasCompletosDelMes;
                const diasSegundoMes = datosDisponibles.analisisPeriodo.segundoMes.diasCompletosDelMes;
                const diasMesPrincipal = Math.max(diasPrimerMes, diasSegundoMes);
                montoSubsidioEnfermedad = parseFloat(((sueldoBasicoEspecial / diasMesPrincipal) * diasSoloSubsidioEnf).toFixed(2));
                console.log(`🔍 Subsidio (0916) dos meses: ${sueldoBasicoEspecial}/${diasMesPrincipal}*${diasSoloSubsidioEnf} = ${montoSubsidioEnfermedad}`);
            }
            const idSub = plameIngreso0916?.id ?? 117;
            Ingresos.push({
                codigoPlame: '0916',
                concepto: plameIngreso0916?.descripcion || 'SUBSIDIOS DE INCAPACIDAD POR ENFERMEDAD',
                id: idSub,
                monto: montoSubsidioEnfermedad,
                plame_configuracion_id: idSub,
                tipo_concepto: 'ingreso',
                ...ingresoSinDescuentoLicGoceHaber()
            });
        }

        console.log(
            `🔍 Procesados - Vacaciones: ${diasEspeciales.vacaciones} días | Feriados/Descansos equiv.: ${totalDiasEquivalentesFeriadosDescansos} d. (feriado 1x:${diasEspeciales.feriado_laborado}, feriado 2x:${diasEspeciales.feriado_laborado_pago_doble}, descanso:${diasEspeciales.descanso_laborado}) | Subsidio (0916): ${diasEspeciales.subsidio_por_enfermedad} d. | Lic. goce (0907): ${diasEspeciales.licencia_con_goce_de_haber} d.`
        );
    } else {
        console.log('⚠️ No se puede calcular conceptos especiales: sueldo básico no válido');
    }

    // 0907 — Ingreso "LICENCIA CON GOCE DE HABER": suma de (monto/día × días tareo) de cada ingreso en config.; no es solo 0121 × días. Independiente de existir sueldo básico.
    const listaPlame0907 = (() => {
        const p = datosDisponibles.apis.conceptosPlame;
        return Array.isArray(p) ? p : Object.values(p || {});
    })();
    const plameIngreso0907 = listaPlame0907.find(c => String(c.codigo_plame) === '0907');
    if (!plameIngreso0907 && diasEspeciales.licencia_con_goce_de_haber > 0) {
        console.warn('⚠️ No hay concepto PLAME 0907 (licencia con goce de haber) en conceptosPlame. Verificar catálogo.');
    }
    const diasSoloLicGoceHaber = diasEspeciales.licencia_con_goce_de_haber;
    const montoAcumulado0907 = Number(
        Ingresos.reduce(
            (s, row) => s + (Number(row.monto_descuento_licencia_goce_haber) || 0),
            0
        ).toFixed(2)
    );
    if (diasSoloLicGoceHaber > 0) {
        console.log(
            `🔍 Lic. con goce (0907): acumulado = Σ monto_descuento_lic. por conceptos en config.: S/ ${montoAcumulado0907} | días tareo: ${diasSoloLicGoceHaber}`
        );
        if (montoAcumulado0907 <= 0) {
            console.warn(
                '⚠️ Hay días tareo licencia_con_goce_de_haber pero el acumulado 0907 es 0: ningún ingreso en la config. o montos 0.'
            );
        }
    }
    if (diasSoloLicGoceHaber > 0 && montoAcumulado0907 > 0) {
        const idLic = plameIngreso0907?.id;
        if (idLic == null) {
            console.error('❌ conceptosPlame no tiene codigo 0907: registrar PLAME 0907 o el ingreso no vinculará a un id válido.');
        }
        Ingresos.push({
            codigoPlame: '0907',
            concepto: plameIngreso0907?.descripcion || 'LICENCIA CON GOCE DE HABER',
            id: idLic ?? 0,
            monto: montoAcumulado0907,
            plame_configuracion_id: idLic ?? 0,
            tipo_concepto: 'ingreso',
            ...ingresoSinDescuentoLicGoceHaber()
        });
    }

    // Conceptos de liquidaciones (calculos_conceptos): solo si el empleado tiene liquidaciones en la planilla
    const liquidacionesPlanilla = Array.isArray(empleado.liquidaciones_planilla) ? empleado.liquidaciones_planilla : [];
    if (liquidacionesPlanilla.length > 0) {
        liquidacionesPlanilla.forEach(liquidacion => {
            const calculosConceptos = Array.isArray(liquidacion.calculos_conceptos) ? liquidacion.calculos_conceptos : [];
            calculosConceptos.forEach(cc => {
                if (cc.monto == null || parseFloat(cc.monto) === 0) return;
                const plameConfig = Object.values(datosDisponibles.apis.conceptosPlame || {}).find(
                    c => c.codigo_plame === cc.codigo_plame
                );
                const plameId = plameConfig?.id ?? cc.id;
                Ingresos.push({
                    codigoPlame: cc.codigo_plame || '',
                    concepto: cc.descripcion || cc.tipo_concepto || '',
                    id: plameId,
                    monto: parseFloat(cc.monto),
                    plame_configuracion_id: plameId,
                    tipo_concepto: 'ingreso',
                    origenCalculo: 'liquidacion',
                    ...ingresoSinDescuentoLicGoceHaber()
                });
            });
        });
    }

    // Gratificaciones julio/diciembre (0406 + 0313): declaración de gratificaciones, NO boleta ordinaria
    const gratificacionesPlanilla = Array.isArray(empleado.gratificaciones_planilla)
        ? empleado.gratificaciones_planilla
        : [];
    if (gratificacionesPlanilla.length > 0) {
        gratificacionesPlanilla.forEach((grat) => {
            const calculosConceptos = Array.isArray(grat.calculos_conceptos) ? grat.calculos_conceptos : [];
            calculosConceptos.forEach((cc) => {
                if (cc.monto == null || parseFloat(cc.monto) === 0) return;
                const plameConfig = Object.values(datosDisponibles.apis.conceptosPlame || {}).find(
                    (c) => c.codigo_plame === cc.codigo_plame
                );
                const plameId = plameConfig?.id ?? cc.id;
                Ingresos.push({
                    codigoPlame: cc.codigo_plame || '',
                    concepto: cc.descripcion || cc.tipo_concepto || '',
                    id: plameId,
                    monto: parseFloat(cc.monto),
                    plame_configuracion_id: plameId,
                    tipo_concepto: 'ingreso',
                    origenCalculo: 'gratificacion',
                    excluirBoletaOrdinaria: true,
                    ...ingresoSinDescuentoLicGoceHaber(),
                });
            });
        });
    }

    // Viáticos standard: informar en planilla/REM, no afectan neto (igual que grati)
    Ingresos.forEach((ing) => {
      const plameId = ing.plame_configuracion_id || ing.id;
      if (!esViaticoDeclaracion(plameId, ing.codigoPlame || ing.codigo)) return;
      ing.origenCalculo = 'viatico';
      ing.excluirBoletaOrdinaria = true;
    });

    
    // ahora vamos a sacar los conceptos de los ingresos del array de datosDisponibles
    console.log('🔍 Array Ingresos final:', Ingresos);

    // ahora vamos a sacar los conceptos de los descuentos del array de datosDisponibles
    const Descuentos = [];
    
    // Convertir conceptos de descuentos de datosDisponibles al formato estándar
    console.log('🔍 Conceptos descuentos disponibles:', datosDisponibles.conceptosDescuentos);
    
    const conceptosDescuentos = datosDisponibles.conceptosDescuentos || [];
    
    conceptosDescuentos.forEach(descuento => {
        console.log('🔍 Procesando descuento:', descuento);
        
        Descuentos.push({
            codigoPlame: descuento.plame_configuracion.codigo_plame,
            concepto: descuento.plame_configuracion.descripcion,
            id: descuento.id,
            monto: parseFloat(descuento.monto),
            plame_configuracion_id: descuento.plame_configuracion.id,
            tipo_concepto: 'descuento'
        });
    });

    // ahora vamos a sacar los conceptos de descuentos de los tareos
    const datosTareosDescuentos = datosDisponibles.tareos?.tareosDetallados ?? [];
    const conceptosDescuentosTareos = [];

    console.log('📋 [DESCUENTOS TAREOS] === EXTRACCIÓN DE CONCEPTOS DE TAREOS PARA DESCUENTOS ===');
    console.log('📋 [DESCUENTOS TAREOS] Origen: datosDisponibles.tareos.tareosDetallados', { totalTareos: datosTareosDescuentos.length, tareos: datosTareosDescuentos });

    const tareosParaDescuentos = datosTareosDescuentos.forEach((tareo, indexTareo) => {
        const conceptosTareo = tareo.conceptos || [];
        if (conceptosTareo.length > 0) {
            console.log(`📋 [DESCUENTOS TAREOS] Tareo ${indexTareo + 1}: fecha=${tareo.fecha} tipo_evento=${tareo.tipo_evento} conceptos.length=${conceptosTareo.length}`);
            const conceptodetallado = conceptosTareo.forEach(concepto => {
                if (concepto.tipo_concepto === 'descuento' || concepto.tipo_concepto === 'adelanto') {
                    const tb = String(concepto.tipo_boleta || 'plame').toLowerCase();
                    if (tb === 'interna') return;

                    const descripcion = concepto.descripcion || concepto.plame_configuracion?.descripcion || concepto.codigo || 'sin descripción';
                    const codigo = concepto.codigo || concepto.plame_configuracion?.codigo_plame || 'sin código';
                    console.log('🔍 [DESCUENTOS TAREOS] Concepto descuento detallado:', { descripcion, codigo, monto: concepto.monto, tipo_concepto: concepto.tipo_concepto, concepto });
                    
                    // Determinar el ID correcto de plame_configuracion
                    const plameId = concepto.plame_configuracion_id || 
                                  concepto.plame_configuracion?.id || 
                                  concepto.id_plame_configuracion ||
                                  concepto.plame_configuracion;
                    
                    conceptosDescuentosTareos.push({
                        id_plame_configuracion: plameId,
                        monto: parseFloat(concepto.monto) || 0,
                    });
                    console.log('🔍 [DESCUENTOS TAREOS] Agregado a conceptosDescuentosTareos:', { id_plame_configuracion: plameId, monto: parseFloat(concepto.monto) || 0 });
                }   
            });
        }
    });
    
    console.log('📋 [DESCUENTOS TAREOS] Total conceptos descuento extraídos de tareos (antes de consolidar):', conceptosDescuentosTareos.length);
    console.log('🔍 [DESCUENTOS TAREOS] Conceptos descuentos de tareos antes de consolidar:', conceptosDescuentosTareos);
    
    // Consolidar conceptos descuentos duplicados sumando sus montos
    const conceptosDescuentosConsolidados = conceptosDescuentosTareos.reduce((acc, concepto) => {
        const existente = acc.find(item => item.id_plame_configuracion === concepto.id_plame_configuracion);
        
        if (existente) {
            // Si ya existe, sumar el monto
            existente.monto += concepto.monto;
            console.log('📋 [DESCUENTOS TAREOS] Consolidando duplicado id_plame=', concepto.id_plame_configuracion, 'monto sumado:', concepto.monto, '-> total acumulado:', existente.monto);
        } else {
            // Si no existe, agregar nuevo
            acc.push({ ...concepto });
        }
        
        return acc;
    }, []);
    
    console.log('📋 [DESCUENTOS TAREOS] Conceptos consolidados (por id_plame_configuracion):', conceptosDescuentosConsolidados.length);
    console.log('🔍 [DESCUENTOS TAREOS] Conceptos descuentos de tareos consolidados:', conceptosDescuentosConsolidados);
    
    // Procesar conceptos descuentos consolidados para agregar a Descuentos
    conceptosDescuentosConsolidados.forEach(conceptoConsolidado => {
        if (conceptoConsolidado.id_plame_configuracion) {
            // Buscar la información completa en conceptosPlame
            const conceptoAfecto = Object.values(datosDisponibles.apis.conceptosPlame).find(correlacion => 
                correlacion.id === conceptoConsolidado.id_plame_configuracion
            );
            
            if (conceptoAfecto) {
                console.log('🔍 [DESCUENTOS TAREOS] Concepto descuento afecto encontrado en conceptosPlame:', conceptoAfecto.codigo_plame, conceptoAfecto.descripcion, 'Monto:', conceptoConsolidado.monto);
                
                Descuentos.push({
                    codigoPlame: conceptoAfecto.codigo_plame,
                    concepto: conceptoAfecto.descripcion,
                    id: conceptoAfecto.id,
                    monto: parseFloat(conceptoConsolidado.monto.toFixed(2)),
                    plame_configuracion_id: conceptoAfecto.id,
                    tipo_concepto: 'descuento'
                });
                console.log('✅ [DESCUENTOS TAREOS] Agregado a array Descuentos:', conceptoAfecto.codigo_plame, conceptoAfecto.descripcion, conceptoConsolidado.monto.toFixed(2));
            } else {
                console.log('⚠️ [DESCUENTOS TAREOS] No se encontró concepto descuento afecto para ID:', conceptoConsolidado.id_plame_configuracion, '(no existe en datosDisponibles.apis.conceptosPlame)');
            }
        }
    });
    
    console.log('📋 [DESCUENTOS TAREOS] === FIN EXTRACCIÓN DESCUENTOS DESDE TAREOS. Total en Descuentos provenientes de tareos:', conceptosDescuentosConsolidados.length);
    
    // ahora vamos a sacar los conceptos de descuentos para faltas y permisos
    const datosTareosFaltasPermisos = datosDisponibles.tareos.tareosDetallados;
    
    // Contar días de faltas y permisos
    const diasDescuentos = {
        falta: 0,
        permiso: 0
    };
    
    // Contar días de faltas y permisos
    datosTareosFaltasPermisos.forEach(tareo => {
        if (tareo.tipo_evento === 'falta') {
            diasDescuentos.falta++;
        } else if (tareo.tipo_evento === 'permiso' || tareo.tipo_evento === 'suspension') {
            diasDescuentos.permiso++;
        }
    });
    
    console.log('🔍 Días de descuentos contados:', diasDescuentos);
    
    // 🎯 CALCULAR BASE PARA DESCUENTOS: SUELDO BÁSICO + CONCEPTOS AFECTOS A TAREOS
    console.log('\n🎯 === CALCULANDO BASE PARA DESCUENTOS ===');
    
    // 1. Obtener sueldo básico (código PLAME 0121)
    const conceptoSueldoBasicoDescuentos = datosDisponibles.conceptosIngresos.find(ingreso => 
        ingreso.plame_configuracion.codigo_plame === '0121'
    );
    const sueldoBasicoDescuentos = conceptoSueldoBasicoDescuentos ? parseFloat(conceptoSueldoBasicoDescuentos.monto) : 0;
    console.log(`💰 Sueldo básico para descuentos: S/ ${sueldoBasicoDescuentos}`);
    console.log('🔍 Conceptos afectos a tareos disponibles:', datosDisponibles.apis.conceptosAfectosTareos);
    console.log('🔍 datosDisponibles.conceptosIngresos:', datosDisponibles.conceptosIngresos);
    // 2. Obtener conceptos afectos a tareos desde datosDisponibles.conceptosIngresos
    const conceptosAfectosTareosEnIngresos = datosDisponibles.conceptosIngresos.filter(ingreso => {
        // Verificar si el concepto está en la lista de conceptos afectos a tareos
        const esAfectoTareos = datosDisponibles.apis.conceptosAfectosTareos.find(
            afecto => afecto.id_concepto_plame === ingreso.plame_configuracion.id
        );
        console.log(`🔍 Verificando concepto [${ingreso.plame_configuracion.codigo_plame}] ${ingreso.plame_configuracion.descripcion}:`);
        console.log(`  - plame_configuracion_id: ${ingreso.plame_configuracion.id}`);
        console.log(`  - Es afecto a tareos: ${!!esAfectoTareos}`);
        return esAfectoTareos && ingreso.plame_configuracion.codigo_plame !== '0121'; // Excluir sueldo básico para evitar duplicado
    });
    
    console.log(`📋 Conceptos afectos a tareos encontrados en Ingresos: ${conceptosAfectosTareosEnIngresos.length}`);
    if (conceptosAfectosTareosEnIngresos.length > 0) {
        conceptosAfectosTareosEnIngresos.forEach((concepto, index) => {
            console.log(`  ${index + 1}. [${concepto.plame_configuracion.codigo_plame}] ${concepto.plame_configuracion.descripcion}: S/ ${concepto.monto}`);
        });
    } else {
        console.log('⚠️ No se encontraron conceptos afectos a tareos en datosDisponibles.conceptosIngresos');
        console.log('🔍 Revisando estructura de conceptosAfectosTareos:');
        datosDisponibles.apis.conceptosAfectosTareos.forEach((afecto, index) => {
            console.log(`  ${index + 1}. id_concepto_plame: ${afecto.id_concepto_plame}`);
        });
    }
    
    // 3. Separar conceptos afectos según sistema_nacional_19990
    const conceptosValidosParaDescuentos = [];
    const conceptosExcluidosSistemaNacional = [];
    
    conceptosAfectosTareosEnIngresos.forEach(concepto => {
        // Buscar el concepto en datosDisponibles.apis.conceptosPlame por codigo_plame
        const conceptoPlame = Object.values(datosDisponibles.apis.conceptosPlame).find(
            plame => plame.codigo_plame === concepto.plame_configuracion.codigo_plame
        );
        
        if (conceptoPlame && conceptoPlame.sistema_nacional_19990 === true) {
            // ✅ Concepto válido para base de descuentos
            conceptosValidosParaDescuentos.push(concepto);
            console.log(`✅ [${concepto.plame_configuracion.codigo_plame}] ${concepto.plame_configuracion.descripcion}: S/ ${concepto.monto} - VÁLIDO para descuentos (sistema_nacional_19990: true)`);
        } else {
            // ❌ Concepto excluido de base de descuentos
            conceptosExcluidosSistemaNacional.push(concepto);
            console.log(`❌ [${concepto.plame_configuracion.codigo_plame}] ${concepto.plame_configuracion.descripcion}: S/ ${concepto.monto} - EXCLUIDO de descuentos (sistema_nacional_19990: ${conceptoPlame?.sistema_nacional_19990 || 'no encontrado'})`);
        }
    });
    
    // 4. Calcular totales separados
    const totalConceptosValidosParaDescuentos = conceptosValidosParaDescuentos.reduce((sum, concepto) => sum + parseFloat(concepto.monto), 0);
    const totalConceptosExcluidosSistemaNacional = conceptosExcluidosSistemaNacional.reduce((sum, concepto) => sum + parseFloat(concepto.monto), 0);
    
    console.log(`💰 Total conceptos VÁLIDOS para descuentos (sistema_nacional_19990 = true): S/ ${totalConceptosValidosParaDescuentos}`);
    console.log(`🚫 Total conceptos EXCLUIDOS de descuentos (sistema_nacional_19990 = false): S/ ${totalConceptosExcluidosSistemaNacional}`);
    console.log(`📊 Total general conceptos afectos a tareos: S/ ${totalConceptosValidosParaDescuentos + totalConceptosExcluidosSistemaNacional}`);
    
    // 5. Calcular base total para descuentos (SOLO conceptos válidos sistema_nacional_19990)
    const baseTotalDescuentos = sueldoBasicoDescuentos + totalConceptosValidosParaDescuentos;
    console.log(`🎯 BASE TOTAL PARA DESCUENTOS: S/ ${baseTotalDescuentos} (Sueldo básico: ${sueldoBasicoDescuentos} + Conceptos válidos: ${totalConceptosValidosParaDescuentos})`);
    
    // 6. Verificar que la base sea válida y definir la base final
    let baseFinalDescuentos = baseTotalDescuentos;
    if (baseTotalDescuentos <= 0) {
        console.log('⚠️ Base total para descuentos no válida - usando solo sueldo básico');
        baseFinalDescuentos = sueldoBasicoDescuentos;
        console.log(`🎯 Base final para descuentos: S/ ${baseFinalDescuentos}`);
    } else {
        console.log(`✅ Base final para descuentos válida: S/ ${baseFinalDescuentos}`);
    }
    
    // FALTAS Y PERMISOS - Código PLAME 0705 (INASISTENCIAS)
    const totalDiasFaltasPermisos = diasDescuentos.falta + diasDescuentos.permiso;
    
    // 📊 DECLARAR VARIABLES PARA CONCEPTOS EXCLUIDOS (fuera de los bloques para acceso global)
    let montoDescuentoInasistenciasExcluidos = 0;
    let montoDescuentoTardanzasExcluidos = 0;
    let montoDescuentoDescansoExcluidos = 0;
    
    if (totalDiasFaltasPermisos > 0 && baseFinalDescuentos > 0) {
        let montoDescuentoInasistencias = 0;
        
        if (datosDisponibles.analisisPeriodo.tipo === 'mes_unico') {
            // Un solo mes
            const diasDelMes = datosDisponibles.analisisPeriodo.mes.diasCompletosDelMes;
            montoDescuentoInasistencias = parseFloat(((baseFinalDescuentos / diasDelMes) * totalDiasFaltasPermisos).toFixed(2));
            
            console.log(`🔍 Descuento Inasistencias mes único: ${baseFinalDescuentos}/${diasDelMes}*${totalDiasFaltasPermisos} = ${montoDescuentoInasistencias}`);
            
            // 📊 CALCULAR TAMBIÉN LOS CONCEPTOS EXCLUIDOS (solo para información/logs)
            montoDescuentoInasistenciasExcluidos = parseFloat(((totalConceptosExcluidosSistemaNacional / diasDelMes) * totalDiasFaltasPermisos).toFixed(2));
            console.log(`🚫 Descuento sobre conceptos EXCLUIDOS (solo informativo): ${totalConceptosExcluidosSistemaNacional}/${diasDelMes}*${totalDiasFaltasPermisos} = ${montoDescuentoInasistenciasExcluidos}`);
            console.log(`📊 COMPARACIÓN: Descuento aplicado = S/ ${montoDescuentoInasistencias} | Descuento excluido = S/ ${montoDescuentoInasistenciasExcluidos} | Diferencia = S/ ${montoDescuentoInasistenciasExcluidos}`);
        } else if (datosDisponibles.analisisPeriodo.tipo === 'dos_meses') {
            // Dos meses - usar el mes con más días como referencia
            const diasPrimerMes = datosDisponibles.analisisPeriodo.primerMes.diasCompletosDelMes;
            const diasSegundoMes = datosDisponibles.analisisPeriodo.segundoMes.diasCompletosDelMes;
            const diasMesPrincipal = Math.max(diasPrimerMes, diasSegundoMes);
            montoDescuentoInasistencias = parseFloat(((baseFinalDescuentos / diasMesPrincipal) * totalDiasFaltasPermisos).toFixed(2));
            console.log(`🔍 Descuento Inasistencias dos meses: ${baseFinalDescuentos}/${diasMesPrincipal}*${totalDiasFaltasPermisos} = ${montoDescuentoInasistencias}`);
            
            // 📊 CALCULAR TAMBIÉN LOS CONCEPTOS EXCLUIDOS (solo para información/logs)
            montoDescuentoInasistenciasExcluidos = parseFloat(((totalConceptosExcluidosSistemaNacional / diasMesPrincipal) * totalDiasFaltasPermisos).toFixed(2));
            console.log(`🚫 Descuento sobre conceptos EXCLUIDOS (solo informativo): ${totalConceptosExcluidosSistemaNacional}/${diasMesPrincipal}*${totalDiasFaltasPermisos} = ${montoDescuentoInasistenciasExcluidos}`);
            console.log(`📊 COMPARACIÓN: Descuento aplicado = S/ ${montoDescuentoInasistencias} | Descuento excluido = S/ ${montoDescuentoInasistenciasExcluidos} | Diferencia = S/ ${montoDescuentoInasistenciasExcluidos}`);
        }

        // Descuento que afectan las faltas a los dias de descanso
        let montoDescuentoDescanso = 0; // Inicializar fuera del bloque
        
        if (datosDisponibles.descansosSemanales.configuracionActivada) {
            console.log(`💤 Configuración descontar_descansos ACTIVADA - aplicando descuentos proporcionales`);
            
            // ⏰ CALCULAR HORAS DE DESCUENTO POR SEMANA
            let totalHorasDescuento = 0;
            const descansoCompletoSemana = 8; // 8 horas de descanso completo por semana
            
            console.log('📋 Calculando horas de descuento por semana:');
            datosDisponibles.descansosSemanales.semanasCalculadas.forEach((semana, index) => {
                // ✅ SOLO PROCESAR SEMANAS CON FECHAS DIFERENTES (NO MISMAS FECHAS)
                if (semana.fechaInicio !== semana.fechaFin) {
                    const horasDescuentoSemana = descansoCompletoSemana - semana.descansoProporcionado;
                    totalHorasDescuento += horasDescuentoSemana;
                    
                    console.log(`  ${index + 1}. ${semana.fechaInicio} - ${semana.fechaFin}:`);
                    console.log(`     Fechas: ${semana.fechaInicio} vs ${semana.fechaFin} (✅ PROCESANDO - fechas diferentes)`);
                    console.log(`     Descanso completo: 8h`);
                    console.log(`     Descanso proporcionado: ${semana.descansoProporcionado.toFixed(2)}h`);
                    console.log(`     Horas a descontar: ${horasDescuentoSemana.toFixed(2)}h`);
                } else {
                    console.log(`  ${index + 1}. ${semana.fechaInicio} - ${semana.fechaFin}:`);
                    console.log(`     Fechas: ${semana.fechaInicio} vs ${semana.fechaFin} (❌ IGNORANDO - misma fecha)`);
                }
            });
            
            console.log(`💰 TOTAL HORAS DE DESCUENTO: ${totalHorasDescuento.toFixed(2)}h`);
            console.log(`📊 Total semanas procesadas: ${datosDisponibles.descansosSemanales.semanasCalculadas.length}`);
            
            // 💰 CALCULAR MONTO DE DESCUENTO SI HAY HORAS A DESCONTAR
            if (totalHorasDescuento > 0) {
                // Calcular monto por hora basado en el sueldo básico
                
                if (datosDisponibles.analisisPeriodo.tipo === 'mes_unico') {
                    const diasDelMes = datosDisponibles.analisisPeriodo.mes.diasCompletosDelMes;
                    const horasDelMes = diasDelMes * 8; // 8 horas por día
                    const montoPorHora = baseFinalDescuentos / diasDelMes / 8;
                    montoDescuentoDescanso = parseFloat((montoPorHora * totalHorasDescuento).toFixed(2));
                    
                    console.log(`🔍 Descuento Descanso mes único: ${baseFinalDescuentos}/${horasDelMes}h*${totalHorasDescuento}h = ${montoDescuentoDescanso}`);
                    
                    // 📊 CALCULAR TAMBIÉN LOS CONCEPTOS EXCLUIDOS PARA DESCANSO (solo para información/logs)
                    const montoPorHoraExcluidos = totalConceptosExcluidosSistemaNacional / diasDelMes / 8;
                    montoDescuentoDescansoExcluidos = parseFloat((montoPorHoraExcluidos * totalHorasDescuento).toFixed(2));
                    console.log(`🚫 Descuento Descanso sobre conceptos EXCLUIDOS (solo informativo): ${totalConceptosExcluidosSistemaNacional}/${horasDelMes}h*${totalHorasDescuento}h = ${montoDescuentoDescansoExcluidos}`);
                    console.log(`📊 COMPARACIÓN DESCANSO: Descuento aplicado = S/ ${montoDescuentoDescanso} | Descuento excluido = S/ ${montoDescuentoDescansoExcluidos} | Diferencia = S/ ${montoDescuentoDescansoExcluidos}`);
                } else if (datosDisponibles.analisisPeriodo.tipo === 'dos_meses') {
                    const diasPrimerMes = datosDisponibles.analisisPeriodo.primerMes.diasCompletosDelMes;
                    const diasSegundoMes = datosDisponibles.analisisPeriodo.segundoMes.diasCompletosDelMes;
                    const diasMesPrincipal = Math.max(diasPrimerMes, diasSegundoMes);
                    const horasDelMes = diasMesPrincipal * 8; // 8 horas por día
                    const montoPorHora = baseFinalDescuentos / horasDelMes;
                    montoDescuentoDescanso = parseFloat((montoPorHora * totalHorasDescuento).toFixed(2));
                    
                    console.log(`🔍 Descuento Descanso dos meses: ${baseFinalDescuentos}/${horasDelMes}h*${totalHorasDescuento}h = ${montoDescuentoDescanso}`);
                    
                    // 📊 CALCULAR TAMBIÉN LOS CONCEPTOS EXCLUIDOS PARA DESCANSO (solo para información/logs)
                    const montoPorHoraExcluidos = totalConceptosExcluidosSistemaNacional / horasDelMes;
                    montoDescuentoDescansoExcluidos = parseFloat((montoPorHoraExcluidos * totalHorasDescuento).toFixed(2));
                    console.log(`🚫 Descuento Descanso sobre conceptos EXCLUIDOS (solo informativo): ${totalConceptosExcluidosSistemaNacional}/${horasDelMes}h*${totalHorasDescuento}h = ${montoDescuentoDescansoExcluidos}`);
                    console.log(`📊 COMPARACIÓN DESCANSO: Descuento aplicado = S/ ${montoDescuentoDescanso} | Descuento excluido = S/ ${montoDescuentoDescansoExcluidos} | Diferencia = S/ ${montoDescuentoDescansoExcluidos}`);
                }
                
                
                console.log(`💤 Descuento por descanso semanal aplicado: ${montoDescuentoDescanso}`);
            } else {
                console.log(`✅ No hay horas de descuento - empleado sin faltas que afecten descanso`);
            }
        }
        
        // ➕ SUMAR DESCUENTO DE DESCANSO CON INASISTENCIAS (siempre, aunque sea 0)
        montoDescuentoInasistencias = parseFloat((montoDescuentoInasistencias + montoDescuentoDescanso).toFixed(2));
        console.log(`🔄 Monto total combinado (Inasistencias + Descuento Descanso): ${montoDescuentoInasistencias}`);
        
        Descuentos.push({
            codigoPlame: '0705',
            concepto: 'INASISTENCIAS',
            id: 293,
            monto: montoDescuentoInasistencias,
            plame_configuracion_id: 293,
            tipo_concepto: 'descuento'
        });
        
        console.log(`🔍 Procesados descuentos - Faltas: ${diasDescuentos.falta} días | Permisos: ${diasDescuentos.permiso} días | Total: ${totalDiasFaltasPermisos} días | Monto final: ${montoDescuentoInasistencias}`);
        
        // necesito los conceptos afectos a tareos
       
    } else {
        if (totalDiasFaltasPermisos === 0) {
            console.log('✅ No hay faltas ni permisos para descontar');
        } else {
            console.log('⚠️ No se puede calcular descuento por inasistencias: base total para descuentos no válida');
        }
    }

    // 🕐 FUNCIÓN PARA CALCULAR HORAS TEÓRICAS BASADAS EN HORARIOS DE ENTRADA Y SALIDA
    // Aplica la misma lógica del backend: descuenta 1 hora de almuerzo si se trabaja más de 6 horas
    const calcularHorasTeoricasDecimales = (horaEntradaTeorica, horaSalidaTeorica) => {
        if (!horaEntradaTeorica || !horaSalidaTeorica) {
            return 8; // Fallback a 8 horas si no hay datos
        }

        try {
            // Parsear horas de entrada y salida (formato HH:MM:SS o HH:MM)
            const [horaEntrada, minutoEntrada] = horaEntradaTeorica.split(':').map(Number);
            const [horaSalida, minutoSalida] = horaSalidaTeorica.split(':').map(Number);
            
            // Convertir a minutos desde medianoche
            const minutosEntrada = horaEntrada * 60 + minutoEntrada;
            const minutosSalida = horaSalida * 60 + minutoSalida;
            
            // Calcular diferencia en minutos
            let diferenciaMinutos = minutosSalida - minutosEntrada;
            
            // Si la salida es menor que la entrada, es del día siguiente
            if (diferenciaMinutos < 0) {
                diferenciaMinutos += 24 * 60; // Agregar 24 horas
            }
            
            // Convertir a horas decimales brutas
            let horasDecimales = diferenciaMinutos / 60;
            
            // 🍽️ APLICAR LA MISMA LÓGICA DEL BACKEND: 
            // Descanso automático de 1 hora si trabajó más de 6 horas
            if (horasDecimales > 6) {
                horasDecimales -= 1;
            }
            
            return Math.max(0, horasDecimales); // Asegurar que no sea negativo
        } catch (error) {
            console.error('Error calculando horas teóricas:', error);
            return 8; // Fallback en caso de error
        }
    };

    // listo ahora para sacar los minutos y compensaciones en tareos (análisis de tardanzas)
    const datosTareosAsistencias = datosDisponibles.tareos.tareosDetallados;
    
    // 💤 OBTENER COMPENSACIONES DE MESES PASADOS PARA ESTE EMPLEADO
    const compensacionesMesesPasados = datosDisponibles.compensacionesMesesPasados;
    const horasCompensadasAnteriores = compensacionesMesesPasados?.horasEmpleado || 0;
    const minutosCompensadosAnteriores = horasCompensadasAnteriores * 60; // Convertir a minutos para precisión
    
    console.log(`💤 === COMPENSACIONES PREVIAS ===`);
    console.log(`💤 Empleado ID: ${empleado.empleado_id} (${empleado.nombre_completo})`);
    console.log(`💤 Horas compensadas anteriores: ${horasCompensadasAnteriores}h (${minutosCompensadosAnteriores} minutos)`);
    console.log(`💤 Estado: ${horasCompensadasAnteriores > 0 ? 'A FAVOR' : horasCompensadasAnteriores < 0 ? 'EN CONTRA' : 'NEUTRO'}`);
    console.log(`💤 Compensación disponible: ${compensacionesMesesPasados?.disponibles ? 'SÍ' : 'NO'}`);
    console.log(`💤 ID compensación: ${compensacionesMesesPasados?.compensacionId || 'N/A'}`);
    
    // Analizar solo asistencias y calcular déficit/superávit de horas del mes actual
    let totalMinutosDeficitSuperavitMesActual = 0;
    const detalleAsistencias = [];
    let totalHorasTeoricasCalculadas = 0;
    let contadorAsistencias = 0;
    
    datosTareosAsistencias.forEach(tareo => {
        if (tareo.tipo_evento === 'asistio') {
            const horasTrabajadas = parseFloat(tareo.horas_trabajadas) || 0;
            
            // 🕐 CALCULAR HORAS ESPERADAS BASADAS EN HORARIOS TEÓRICOS REALES
            const horasEsperadas = calcularHorasTeoricasDecimales(
                tareo.hora_entrada_teorica, 
                tareo.hora_salida_teorica
            );
            
            const diferenciaHoras = horasTrabajadas - horasEsperadas;
            const diferenciaMinutos = diferenciaHoras * 60;
            
            totalHorasTeoricasCalculadas += horasEsperadas;
            contadorAsistencias++;
            
            // Valores a 2 decimales para el detalle; si la diferencia en horas es 0, minutos debe ser 0
            const ht = parseFloat(Number(horasTrabajadas).toFixed(2));
            const he = parseFloat(Number(horasEsperadas).toFixed(2));
            const dh = parseFloat(Number(diferenciaHoras).toFixed(2));
            const dm = dh === 0 ? 0 : parseFloat(Number(diferenciaMinutos).toFixed(2));
            
            totalMinutosDeficitSuperavitMesActual += dm; // Acumular con el mismo valor del detalle (redondeado)
            
            detalleAsistencias.push({
                fecha: tareo.fecha,
                horasTrabajadas: ht,
                horasEsperadas: he,
                diferenciaHoras: dh,
                diferenciaMinutos: dm,
                estado: dm < 0 ? 'déficit' : (dm > 0 ? 'superávit' : 'exacto'),
                horariosTeoricoUsados: `${tareo.hora_entrada_teorica}-${tareo.hora_salida_teorica}`
            });
            
            //console.log(`🔍 ${tareo.fecha}: ${horasTrabajadas}h trabajadas vs ${horasEsperadas}h esperadas (${tareo.hora_entrada_teorica}-${tareo.hora_salida_teorica}) = ${diferenciaMinutos.toFixed(2)} minutos (${diferenciaMinutos < 0 ? 'déficit' : 'superávit'})`);
        }
    });
    
    // Total del mes redondeado a 2 decimales (coherente con el detalle)
    totalMinutosDeficitSuperavitMesActual = parseFloat(Number(totalMinutosDeficitSuperavitMesActual).toFixed(2));
    
    // 🧮 CÁLCULO INTEGRADO: COMPENSACIONES ANTERIORES + DÉFICIT/SUPERÁVIT ACTUAL
    const totalMinutosDeficitSuperavit = minutosCompensadosAnteriores + totalMinutosDeficitSuperavitMesActual;
    const totalHorasDeficitSuperavit = totalMinutosDeficitSuperavit / 60;
    
    console.log(`\n🧮 === CÁLCULO INTEGRADO DE COMPENSACIONES ===`);
    console.log(`📊 Minutos compensados anteriores: ${minutosCompensadosAnteriores >= 0 ? '+' : ''}${minutosCompensadosAnteriores.toFixed(2)} min`);
    console.log(`📊 Minutos déficit/superávit mes actual: ${totalMinutosDeficitSuperavitMesActual >= 0 ? '+' : ''}${totalMinutosDeficitSuperavitMesActual.toFixed(2)} min`);
    console.log(`📊 TOTAL INTEGRADO: ${totalMinutosDeficitSuperavit >= 0 ? '+' : ''}${totalMinutosDeficitSuperavit.toFixed(2)} min (${totalHorasDeficitSuperavit >= 0 ? '+' : ''}${totalHorasDeficitSuperavit.toFixed(2)}h)`);
    console.log(`🎯 Decisión: ${totalMinutosDeficitSuperavit < 0 ? '✅ APLICAR DESCUENTO' : '❌ NO APLICAR DESCUENTO'} ${totalMinutosDeficitSuperavit > 0 ? '(Crédito para próximo mes)' : totalMinutosDeficitSuperavit === 0 ? '(Balanceado exacto)' : '(Déficit final)'}`);
    console.log(`===============================================\n`);
    
    // Calcular promedio de horas teóricas para usar en cálculos posteriores
    const promedioHorasTeoricasDiarias = contadorAsistencias > 0 ? totalHorasTeoricasCalculadas / contadorAsistencias : 8;
    
    console.log('🔍 Detalle de asistencias:', detalleAsistencias);
    console.log('🔍 Total minutos déficit/superávit MES ACTUAL:', totalMinutosDeficitSuperavitMesActual.toFixed(2));
    console.log(`🕐 === RESUMEN HORAS TEÓRICAS CALCULADAS ===`);
    console.log(`🕐 Total horas teóricas acumuladas: ${totalHorasTeoricasCalculadas.toFixed(2)}h`);
    console.log(`🕐 Asistencias procesadas: ${contadorAsistencias} días`);
    console.log(`🕐 Promedio horas teóricas diarias: ${promedioHorasTeoricasDiarias.toFixed(2)}h (reemplaza el valor fijo de 8h)`);
    console.log(`🕐 ============================================`);
    
    // Si hay déficit total (negativo), aplicar descuento por tardanzas
    if (totalMinutosDeficitSuperavit < 0) {
        // Usar la base final calculada para descuentos (sueldo básico + conceptos afectos a tareos)
        if (baseFinalDescuentos > 0) {
            // Calcular descuento por tardanzas
            const minutosDeficit = Math.abs(totalMinutosDeficitSuperavit);
            const horasDeficit = minutosDeficit / 60;
            
            let montoDescuentoTardanzas = 0;
            
            if (datosDisponibles.analisisPeriodo.tipo === 'mes_unico') {
                // Un solo mes
                const diasDelMes = datosDisponibles.analisisPeriodo.mes.diasCompletosDelMes;
                const valorHoraDiaria = baseFinalDescuentos / diasDelMes / promedioHorasTeoricasDiarias; // Valor hora = base final / días / horas teóricas promedio
                montoDescuentoTardanzas = parseFloat((valorHoraDiaria * horasDeficit).toFixed(2));
                console.log(`🔍 Descuento Tardanzas mes único: (${baseFinalDescuentos}/${diasDelMes}/${promedioHorasTeoricasDiarias.toFixed(2)})*${horasDeficit.toFixed(2)} = ${montoDescuentoTardanzas}`);
                
                // 📊 CALCULAR TAMBIÉN LOS CONCEPTOS EXCLUIDOS PARA TARDANZAS (solo para información/logs)
                const valorHoraDiariaExcluidos = totalConceptosExcluidosSistemaNacional / diasDelMes / promedioHorasTeoricasDiarias;
                montoDescuentoTardanzasExcluidos = parseFloat((valorHoraDiariaExcluidos * horasDeficit).toFixed(2));
                console.log(`🚫 Descuento Tardanzas sobre conceptos EXCLUIDOS (solo informativo): (${totalConceptosExcluidosSistemaNacional}/${diasDelMes}/${promedioHorasTeoricasDiarias.toFixed(2)})*${horasDeficit.toFixed(2)} = ${montoDescuentoTardanzasExcluidos}`);
                console.log(`📊 COMPARACIÓN TARDANZAS: Descuento aplicado = S/ ${montoDescuentoTardanzas} | Descuento excluido = S/ ${montoDescuentoTardanzasExcluidos} | Diferencia = S/ ${montoDescuentoTardanzasExcluidos}`);
            } else if (datosDisponibles.analisisPeriodo.tipo === 'dos_meses') {
                // Dos meses
                const diasPrimerMes = datosDisponibles.analisisPeriodo.primerMes.diasCompletosDelMes;
                const diasSegundoMes = datosDisponibles.analisisPeriodo.segundoMes.diasCompletosDelMes;
                const diasMesPrincipal = Math.max(diasPrimerMes, diasSegundoMes);
                const valorHoraDiaria = baseFinalDescuentos / diasMesPrincipal / promedioHorasTeoricasDiarias;
                montoDescuentoTardanzas = parseFloat((valorHoraDiaria * horasDeficit).toFixed(2));
                console.log(`🔍 Descuento Tardanzas dos meses: (${baseFinalDescuentos}/${diasMesPrincipal}/${promedioHorasTeoricasDiarias.toFixed(2)})*${horasDeficit.toFixed(2)} = ${montoDescuentoTardanzas}`);
                
                // 📊 CALCULAR TAMBIÉN LOS CONCEPTOS EXCLUIDOS PARA TARDANZAS (solo para información/logs)
                const valorHoraDiariaExcluidos = totalConceptosExcluidosSistemaNacional / diasMesPrincipal / promedioHorasTeoricasDiarias;
                montoDescuentoTardanzasExcluidos = parseFloat((valorHoraDiariaExcluidos * horasDeficit).toFixed(2));
                console.log(`🚫 Descuento Tardanzas sobre conceptos EXCLUIDOS (solo informativo): (${totalConceptosExcluidosSistemaNacional}/${diasMesPrincipal}/${promedioHorasTeoricasDiarias.toFixed(2)})*${horasDeficit.toFixed(2)} = ${montoDescuentoTardanzasExcluidos}`);
                console.log(`📊 COMPARACIÓN TARDANZAS: Descuento aplicado = S/ ${montoDescuentoTardanzas} | Descuento excluido = S/ ${montoDescuentoTardanzasExcluidos} | Diferencia = S/ ${montoDescuentoTardanzasExcluidos}`);
            }
            
            Descuentos.push({
                codigoPlame: '0704',
                concepto: 'TARDANZAS',
                id: 292,
                monto: montoDescuentoTardanzas,
                plame_configuracion_id: 292,
                tipo_concepto: 'descuento'
            });
            
            console.log(`🔍 Procesado descuento por tardanzas - Total déficit: ${minutosDeficit.toFixed(2)} minutos (${horasDeficit.toFixed(2)} horas) | Monto: S/ ${montoDescuentoTardanzas}`);
        } else {
            console.log('⚠️ No se puede calcular descuento por tardanzas: base total para descuentos no válida');
        }
    } else if (totalMinutosDeficitSuperavit > 0) {
        console.log(`✅ Empleado tiene superávit de ${totalMinutosDeficitSuperavit.toFixed(2)} minutos - No hay descuento por tardanzas`);
    } else {
        console.log('✅ Empleado cumplió exactamente las horas esperadas - No hay descuento por tardanzas');
    }

    // Si la suma de los excluidos es mayor a cero, se van a otros descuentos no relacionados a la base imponible
    if (totalConceptosExcluidosSistemaNacional > 0) {
        console.log('🔍 Suma de conceptos excluidos:', totalConceptosExcluidosSistemaNacional);
        
        // 📊 CALCULAR SUMA TOTAL DE DESCUENTOS EXCLUIDOS
        const totalDescuentosExcluidos = montoDescuentoInasistenciasExcluidos + montoDescuentoTardanzasExcluidos + montoDescuentoDescansoExcluidos;
        
        console.log(`\n🚫 === OTROS DESCUENTOS NO DEDUCIBLES DE LA BASE IMPONIBLE ===`);
        console.log(`💰 Descuento Inasistencias excluido: S/ ${montoDescuentoInasistenciasExcluidos.toFixed(2)}`);
        console.log(`💰 Descuento Tardanzas excluido: S/ ${montoDescuentoTardanzasExcluidos.toFixed(2)}`);
        console.log(`💰 Descuento Descanso excluido: S/ ${montoDescuentoDescansoExcluidos.toFixed(2)}`);
        console.log(`🎯 TOTAL DESCUENTOS EXCLUIDOS: S/ ${totalDescuentosExcluidos.toFixed(2)}`);
        
        // Solo agregar el concepto si hay monto total a descontar
        if (totalDescuentosExcluidos > 0) {
            Descuentos.push({
                codigoPlame: '0706',
                concepto: 'OTROS DESCUENTOS NO DEDUCIBLES DE LA BASE IMPONIBLE',
                id: 294,
                monto: parseFloat(totalDescuentosExcluidos.toFixed(2)),
                plame_configuracion_id: 294,
                tipo_concepto: 'descuento'
            });
            
            console.log(`✅ Concepto 0706 agregado con monto: S/ ${totalDescuentosExcluidos.toFixed(2)}`);
        } else {
            console.log(`ℹ️ No se agrega concepto 0706 - monto total es 0`);
        }
        console.log(`================================================================\n`);
    }

    console.log('🔍 Array Descuentos final:', Descuentos);

  // Listo pasamos a Aportes
  const Aportes = [];
  
  // Convertir conceptos de aportes de datosDisponibles al formato estándar
  console.log('🔍 Conceptos aportes disponibles:', datosDisponibles.conceptosAportes);
  
  const conceptosAportes = datosDisponibles.conceptosAportes || [];
  
  conceptosAportes.forEach(aporte => {
      console.log('🔍 Procesando aporte:', aporte);

      const codigoPlame = String(
        aporte.plame_configuracion?.codigo_plame ||
        aporte.codigo_plame ||
        aporte.codigoPlame ||
        aporte.codigo ||
        ''
      );
      const descripcion =
        aporte.plame_configuracion?.descripcion ||
        aporte.descripcion ||
        aporte.concepto ||
        '';
      const plameId =
        aporte.plame_configuracion?.id ||
        aporte.plame_configuracion_id ||
        aporte.id;
      const monto = parseFloat(aporte.monto);

      if (!codigoPlame || Number.isNaN(monto) || monto <= 0) {
        return;
      }

      Aportes.push({
          codigoPlame,
          concepto: descripcion,
          id: plameId,
          monto,
          plame_configuracion_id: plameId,
          tipo_concepto: 'aporte'
      });
  });
  
  console.log('🔍 Array Aportes inicial:', Aportes);

  // RH (Recibo por Honorarios): ingresos + descuentos operativos (faltas, tardanzas, etc.).
  // No aplica descuentos pensionarios/renta ni aportes de planilla (AFP, ONP, EsSalud).
  const tipoContratacionReal = String(
    empleado.datosSalariales?.tipoContratacion ||
    empleado.datosEmpleado?.tipo_contratacion ||
    'Planilla'
  ).trim();
  const esReciboHonorarios =
    tipoContratacionReal === 'Recibo por Honorarios' ||
    tipoContratacionReal.toLowerCase().includes('honorario');

  const CODIGOS_DESCUENTO_PLANILLA_RH = [
    '0601', '0605', '0606', '0607', '0608', '0611', '0616',
  ];
  const descuentosFinales = esReciboHonorarios
    ? Descuentos.filter((d) => {
        const codigo = String(d.codigoPlame || d.codigo || '');
        return !CODIGOS_DESCUENTO_PLANILLA_RH.includes(codigo);
      })
    : Descuentos;
  const aportesFinales = esReciboHonorarios ? [] : Aportes;

  if (esReciboHonorarios) {
    console.log(
      `📋 Empleado RH: se conservan descuentos operativos (${descuentosFinales.length}) ` +
      `y se omiten pensionarios/aportes de planilla`
    );
  }

  // Preparar estructura conceptosUsuario para el calcularBoletaService
  const conceptosUsuario = {
    ingresos: Ingresos,
    descuentos: descuentosFinales,
    aportes: aportesFinales
  };

  console.log('🔍 Conceptos Usuario preparados:', conceptosUsuario);

  // Calcular totales (boleta ordinaria: sin ingresos solo-declaración)
  const totalIngresosOrdinarios = Ingresos
    .filter((item) => !(item.excluirBoletaOrdinaria || item.excluir_boleta_ordinaria))
    .reduce((sum, item) => sum + item.monto, 0);
  const totales = {
    totalIngresos: totalIngresosOrdinarios,
    totalDescuentos: descuentosFinales.reduce((sum, item) => sum + item.monto, 0),
    totalAportes: aportesFinales.reduce((sum, item) => sum + item.monto, 0)
  };

  console.log('🔍 Totales calculados:', totales);

  // Importar y llamar al calculosBoletaService
  const calculosBoletaService = require('./calculosBoletaService').default;
  
  // Buscar el nombre completo del régimen laboral
  const codigoRegimen = empleado.datosSalariales.regimenLaboral;
  const regimenCompleto = empleado.datosAPIs.regimenesLaborales.find(
    regimen => regimen.codigo === codigoRegimen
  );
  const nombreRegimenCompleto = regimenCompleto ? regimenCompleto.nombre : codigoRegimen;
  
  console.log('🔍 Código régimen original:', codigoRegimen);
  console.log('🔍 Régimen encontrado:', regimenCompleto);
  console.log('🔍 Nombre completo del régimen:', nombreRegimenCompleto);

  // Preparar datos para el service
  const tipoPlanillaRaw = String(
    empleado.datosSalariales?.tipoPlanilla ||
    empleado.datosEmpleado?.tipo_planilla ||
    ''
  ).toLowerCase();
  const esSoloInterna = tipoPlanillaRaw === 'solo_interna';
  const esDual = tipoPlanillaRaw === 'dual';
  const fusionInterna = fusionarAjustesEnInterna({
    ingresos: empleado.conceptosUsuario?.ingresos,
    descuentos: empleado.conceptosUsuario?.descuentos,
    ingresosInternos: empleado.conceptosUsuario?.ingresosInternos,
    descuentosInternos: empleado.conceptosUsuario?.descuentosInternos,
  });
  const sueldoInterno = Number(
    fusionInterna.sueldoInternoOverride
    ?? empleado.datosSalariales?.sueldoInterno
    ?? empleado.datosEmpleado?.sueldo_interno
    ?? 0
  );

  const tipoContratacionParaMotor = (() => {
    if (esSoloInterna) return 'Interno';
    if (esReciboHonorarios) return 'Recibo por Honorarios';
    return 'Planilla';
  })();

  const datosCompletos = {
    datosEmpresa: contexto.contexto?.empresa,
    datosAPIs: empleado.datosAPIs,
    omitir_descuentos_plame_interna: Boolean(
      empleado.omitir_descuentos_plame_interna
      ?? empleado.datosSalariales?.omitirDescuentosPlameInterna
      ?? empleado.datosEmpleado?.omitir_descuentos_plame_interna
    ),
    datosSalariales: {
      ...empleado.datosSalariales,
      sueldoBasico: esSoloInterna
        ? (sueldoInterno || Number(empleado.datosSalariales?.sueldoBasico) || 0)
        : empleado.datosSalariales?.sueldoBasico,
      sueldoInterno,
      tipoPlanilla: tipoPlanillaRaw || null,
      fechaIngresoInterna:
        empleado.datosSalariales?.fechaIngresoInterna
        || empleado.datosEmpleado?.fecha_ingreso_interna
        || empleado.datosEmpleado?.fechaIngresoInterna
        || null,
      omitirDescuentosPlameInterna: Boolean(
        empleado.omitir_descuentos_plame_interna
        ?? empleado.datosSalariales?.omitirDescuentosPlameInterna
        ?? empleado.datosEmpleado?.omitir_descuentos_plame_interna
      ),
      // Planilla normal fuerza Planilla; RH conserva Recibo; solo_interna = Interno
      tipoContratacion: tipoContratacionParaMotor,
      regimenLaboral: nombreRegimenCompleto // ✅ Reemplazar código por nombre completo
    },
    conceptosUsuario: {
      ...conceptosUsuario,
      ingresosInternos: (() => {
        const base = Array.isArray(fusionInterna.ingresosInternos)
          ? [...fusionInterna.ingresosInternos]
          : [];
            // Conceptos de tareo marcados como boleta interna (p. ej. viáticos)
        const tareos = datosDisponibles?.tareos?.tareosDetalladosInterna
          || datosDisponibles?.tareos?.tareosDetallados
          || empleado.tareosEmpleadoInterna
          || empleado.tareosEmpleadoFiltrados
          || empleado.tareosEmpleado
          || [];
        const conceptosPlameMap = datosDisponibles?.apis?.conceptosPlame || {};
        (Array.isArray(tareos) ? tareos : []).forEach((tareo) => {
          (tareo.conceptos || []).forEach((c) => {
            if (String(c.tipo_boleta || 'plame').toLowerCase() !== 'interna') return;
            if (!['ingreso'].includes(String(c.tipo_concepto || ''))) return;
            const plameId = c.plame_configuracion_id || c.plame_configuracion?.id;
            const plameFromApi = plameId
              ? (Object.values(conceptosPlameMap).find((p) => Number(p.id) === Number(plameId)) || null)
              : null;
            const codigo = c.plame_configuracion?.codigo_plame
              || plameFromApi?.codigo_plame
              || c.codigo_plame
              || c.codigo
              || '';
            const monto = parseFloat(c.monto) || 0;
            if (!codigo || monto <= 0) return;
            const existente = base.find((x) => String(
              x.codigoPlame
              || x.codigo_plame
              || x.codigo
              || x.plame_configuracion?.codigo_plame
              || x.plameConfiguracion?.codigo_plame
              || ''
            ) === String(codigo));
            if (existente) {
              existente.monto = (parseFloat(existente.monto) || 0) + monto;
              existente.origenTareo = true;
              existente.origen_tareo = true;
              existente.montoTareo = (parseFloat(existente.montoTareo) || 0) + monto;
              if (esViaticoDeclaracion(plameId, codigo)) {
                existente.origenCalculo = 'viatico';
                existente.excluirBoletaOrdinaria = true;
              } else if (!existente.origenCalculo) {
                existente.origenCalculo = 'tareo';
              }
              return;
            }
            base.push({
              id: plameId,
              codigo,
              codigo_plame: codigo,
              codigoPlame: codigo,
              monto,
              montoTareo: monto,
              tipo_concepto: c.tipo_concepto,
              descripcion: c.plame_configuracion?.descripcion
                || plameFromApi?.descripcion
                || c.descripcion
                || codigo,
              observaciones: c.observaciones || null,
              origenTareo: true,
              origen_tareo: true,
              origenCalculo: esViaticoDeclaracion(plameId, codigo) ? 'viatico' : 'tareo',
              ...(esViaticoDeclaracion(plameId, codigo)
                ? { excluirBoletaOrdinaria: true }
                : {}),
            });
          });
        });
        return base;
      })(),
      descuentosInternos: (() => {
        const base = Array.isArray(fusionInterna.descuentosInternos)
          ? [...fusionInterna.descuentosInternos]
          : [];
        const tareos = datosDisponibles?.tareos?.tareosDetalladosInterna
          || datosDisponibles?.tareos?.tareosDetallados
          || empleado.tareosEmpleadoInterna
          || empleado.tareosEmpleadoFiltrados
          || empleado.tareosEmpleado
          || [];
        const conceptosPlameMap = datosDisponibles?.apis?.conceptosPlame || {};
        (Array.isArray(tareos) ? tareos : []).forEach((tareo) => {
          (tareo.conceptos || []).forEach((c) => {
            if (String(c.tipo_boleta || 'plame').toLowerCase() !== 'interna') return;
            if (!['descuento', 'adelanto'].includes(String(c.tipo_concepto || ''))) return;
            const plameId = c.plame_configuracion_id || c.plame_configuracion?.id;
            const plameFromApi = plameId
              ? (Object.values(conceptosPlameMap).find((p) => Number(p.id) === Number(plameId)) || null)
              : null;
            const codigo = c.plame_configuracion?.codigo_plame
              || plameFromApi?.codigo_plame
              || c.codigo_plame
              || c.codigo
              || '';
            const monto = parseFloat(c.monto) || 0;
            if (!codigo || monto <= 0) return;
            const existente = base.find((x) => String(
              x.codigoPlame
              || x.codigo_plame
              || x.codigo
              || x.plame_configuracion?.codigo_plame
              || x.plameConfiguracion?.codigo_plame
              || ''
            ) === String(codigo));
            if (existente) {
              existente.monto = (parseFloat(existente.monto) || 0) + monto;
              existente.origenTareo = true;
              existente.origen_tareo = true;
              existente.montoTareo = (parseFloat(existente.montoTareo) || 0) + monto;
              if (!existente.origenCalculo) existente.origenCalculo = 'tareo';
              return;
            }
            base.push({
              id: plameId,
              codigo,
              codigo_plame: codigo,
              codigoPlame: codigo,
              monto,
              montoTareo: monto,
              tipo_concepto: c.tipo_concepto,
              descripcion: c.plame_configuracion?.descripcion
                || plameFromApi?.descripcion
                || c.descripcion
                || codigo,
              observaciones: c.observaciones || null,
              origenTareo: true,
              origen_tareo: true,
              origenCalculo: 'tareo',
            });
          });
        });
        return base;
      })(),
    },
    datosEmpleado: {
      ...empleado.datosEmpleado,
      tipo_planilla: tipoPlanillaRaw || empleado.datosEmpleado?.tipo_planilla || null,
      sueldo_interno: sueldoInterno,
    },
    periodo: contexto.contexto?.periodo,
    planilla: contexto.contexto?.planilla_real
  };

  console.log('🔍 Datos preparados para calculosBoletaService:', datosCompletos);

  // Llamar al service para calcular la boleta PLAME (omitir si solo_interna)
  let resultadoBoleta = null;
  if (!esSoloInterna) {
    resultadoBoleta = await calculosBoletaService.generarCalculos(datosCompletos, 'planilla_calculo_service');
    console.log('🔍 Resultado de la boleta PLAME:', resultadoBoleta);
  } else {
    console.log('ℹ️ Empleado solo_interna: se omite motor PLAME');
  }

  // Boleta interna (control) cuando hay dual o solo_interna
  let resultadoInterna = null;
  let resultadoInterna1 = null;
  let resultadoInterna2 = null;
  let esBoletaInternaPartida = false;
  if (esDual || esSoloInterna) {
    const { calcularBoletaInternaUna } = require('../utils/calcularBoletaInternaTramo');
    const {
      tramoActivoParaEmpleado,
      partirTareosPorTramos,
      mergeResultadosBoletaInterna,
    } = require('../utils/boletaInternaTramos');

    const tareosParaEspeciales =
      datosDisponibles?.tareos?.tareosDetalladosInterna
      || datosDisponibles?.tareos?.tareosDetallados
      || empleado.tareosEmpleadoFiltrados
      || empleado.tareosEmpleado
      || [];
    const sueldoBaseInternaDefault = sueldoInterno || Number(datosCompletos.datosSalariales?.sueldoBasico) || 0;

    const omitirDescFlag = Boolean(
      datosCompletos.omitir_descuentos_plame_interna
      ?? datosCompletos.datosSalariales?.omitirDescuentosPlameInterna
    );

    const mapaTramos =
      empleado.tramosBoletaInterna
      || contexto.contexto?.tramos_boleta_interna
      || contexto.arrayUnificado?.find((e) => e.tipo === 'contexto_empresarial')?.tramos_boleta_interna
      || {};
    const configTramoBoleta = tramoActivoParaEmpleado(mapaTramos, empleado.empleado_id);
    esBoletaInternaPartida = Boolean(configTramoBoleta);
    let configTramo = configTramoBoleta;
    if (!configTramo) {
      const mapaSueldo =
        empleado.tramosSueldoPlame
        || contexto.contexto?.tramos_sueldo_plame
        || contexto.arrayUnificado?.find((e) => e.tipo === 'contexto_empresarial')?.tramos_sueldo_plame
        || {};
      const cfgSueldo = tramoSueldoPlameActivo(mapaSueldo, empleado.empleado_id);
      if (cfgSueldo && destinoAplicaInterna(cfgSueldo)) {
        configTramo = configInternaDesdeSueldoTramo(cfgSueldo, tareosParaEspeciales);
      }
    }

    const optsBase = {
      datosCompletos,
      resultadoBoleta,
      datosDisponibles,
      empleado,
      esSoloInterna,
      conceptosUsuario,
      promedioHorasTeoricasDiarias:
        typeof promedioHorasTeoricasDiarias === 'number' ? promedioHorasTeoricasDiarias : 8,
    };

    if (configTramo) {
      const { tareos1, tareos2 } = partirTareosPorTramos(
        tareosParaEspeciales,
        configTramo.fechas_tramo_1
      );
      const destino = String(configTramo.destino_descuentos_plame || 'interna_1');
      const minutos =
        typeof totalMinutosDeficitSuperavit === 'number' ? totalMinutosDeficitSuperavit : 0;

      resultadoInterna1 = await calcularBoletaInternaUna({
        ...optsBase,
        sueldoBaseInterna: Number(configTramo.sueldo_interno_1) || 0,
        tareos: tareos1,
        omitirDescPlameInterna: omitirDescFlag || destino !== 'interna_1',
        incluirPlantilla: true,
        minutosDeficit: minutos,
        etiqueta: 'planilla_calculo_interna_1',
        prorratearPorDiasTramo: true,
      });
      resultadoInterna2 = await calcularBoletaInternaUna({
        ...optsBase,
        sueldoBaseInterna: Number(configTramo.sueldo_interno_2) || 0,
        tareos: tareos2,
        omitirDescPlameInterna: omitirDescFlag || destino !== 'interna_2',
        incluirPlantilla: false,
        minutosDeficit: 0,
        etiqueta: 'planilla_calculo_interna_2',
        prorratearPorDiasTramo: true,
      });
      resultadoInterna = mergeResultadosBoletaInterna(resultadoInterna1, resultadoInterna2);
      if (resultadoInterna && !esBoletaInternaPartida) {
        resultadoInterna.sueldo_partido = {
          dias1: resultadoInterna1?.dias_tramo ?? null,
          dias2: resultadoInterna2?.dias_tramo ?? null,
          sueldo1: Number(configTramo.sueldo_interno_1) || 0,
          sueldo2: Number(configTramo.sueldo_interno_2) || 0,
        };
      }
      console.log('🔍 Boleta interna en 2 tramos (días repartidos):', {
        destino,
        dias1: resultadoInterna1?.dias_tramo,
        dias2: resultadoInterna2?.dias_tramo,
        sueldo1: configTramo.sueldo_interno_1,
        sueldo2: configTramo.sueldo_interno_2,
        monto0121_1: resultadoInterna1?.sueldo_0121_prorrateado,
        monto0121_2: resultadoInterna2?.sueldo_0121_prorrateado,
      });
    } else {
      resultadoInterna = await calcularBoletaInternaUna({
        ...optsBase,
        sueldoBaseInterna: sueldoBaseInternaDefault,
        tareos: tareosParaEspeciales,
        omitirDescPlameInterna: omitirDescFlag,
        incluirPlantilla: true,
        minutosDeficit:
          typeof totalMinutosDeficitSuperavit === 'number' ? totalMinutosDeficitSuperavit : 0,
        etiqueta: 'planilla_calculo_interna',
      });
      console.log('🔍 Resultado de la boleta interna:', resultadoInterna);
    }
  }
  
  // 🔍 LOG ESPECÍFICO PARA EMPLEADO SIN BOLETA VÁLIDA
  const boletaPrincipal = resultadoBoleta || (esSoloInterna ? resultadoInterna : null);
  if (!boletaPrincipal || !boletaPrincipal.success || !boletaPrincipal.data) {
    console.error(`❌ === PROBLEMA CON BOLETA DEL EMPLEADO ${empleado.empleado_id} ===`);
    console.error(`   Nombre: ${empleado.empleado_nombre}`);
    console.error(`   Resultado PLAME:`, JSON.stringify(resultadoBoleta, null, 2));
    console.error(`   Resultado interna:`, JSON.stringify(resultadoInterna, null, 2));
  }

  // Retornar el empleado con la boleta calculada (+ interna si aplica)
  const empleadoProcesado = {
    ...empleado,
    conceptosUsuario: conceptosUsuario,
    totales: totales,
    // Compat: boleta principal = PLAME; en solo_interna = interna
    boleta: boletaPrincipal,
    boleta_interna: resultadoInterna,
    boleta_interna_1: resultadoInterna1,
    boleta_interna_2: resultadoInterna2,
    boleta_interna_partida: esBoletaInternaPartida,
    tipo_planilla: tipoPlanillaRaw || null,
    sueldo_interno: sueldoInterno,
    procesamiento: {
      timestamp: new Date().toISOString(),
      estado: 'procesado_con_boleta',
      modificaciones: [],
      observaciones: esSoloInterna
        ? 'Procesamiento exitoso - boleta interna'
        : (esDual
          ? 'Procesamiento exitoso - boleta PLAME + interna'
          : 'Procesamiento exitoso - boleta calculada'),
      calculosRealizados: {
        totalIngresos: totales.totalIngresos,
        totalDescuentos: totales.totalDescuentos,
        totalAportes: totales.totalAportes,
        netoAPagar: totales.totalIngresos - totales.totalDescuentos
      }
    }
  };
  
  return empleadoProcesado;
}

export default planillaCalculoService; 